'use client';
import { BarChart3, ClipboardList, DollarSign, FileText, Folder, Lightbulb, Package, Wrench, XCircle, MessageSquare, FolderOpen, Image as ImageIcon, RefreshCw, Archive, ChevronUp, ChevronDown, ChevronRight, Bot, Paperclip, Send, Box, PlusCircle, Download, HardDrive, Trash2, PanelRightOpen, PanelRightClose, Upload, Plus, Edit3, Check, Loader2, Maximize, ExternalLink, Calculator, ArrowRight, Settings, Sparkles, LayoutList } from 'lucide-react';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useTheme } from '@/components/ThemeContext';
import { generateEstimatePDF } from '@/lib/furniturePdf';
import { calcCostBreakdown, estimateMaterial, CostBreakdown, getCategoryImageUrl, PRICE_MODELS, generateEstimate as generateEstimateLogic } from '@/lib/furniturePricing';
import { generateEstimateXlsx } from '@/lib/furnitureXlsx';
import { putDrawings } from '@/lib/uploads/drawingStore';
import OmniChatPanel from '@/components/OmniChatPanel';
import LaborRatePicker from '@/components/furniture-agent/LaborRatePicker';
import { useJobStream } from "./_components/useJobStream";
import { PipelineJobCard } from "./_components/PipelineJobCard";
import { ActionLogRoller } from "./_components/ActionLogRoller";
import dynamic from 'next/dynamic';

// SSR 비활성화 (WebGL은 브라우저 전용)
const FurnitureViewer3D = dynamic(() => import('@/components/FurnitureViewer3D'), { ssr: false });
const BulkEstimateTab = dynamic(() => import('@/components/BulkEstimateTab'), { ssr: false });

/* ── 타입 ── */
interface FurnitureItem {
  no: number; name: string; code: string; location: string;
  w: number; d: number; h: number; qty: number; category: string;
  surfaceM2: number; volumeM3: number; unitPrice: number; totalPrice: number;
  priceGrade: string; basis: string;
  costBreakdown?: {
    materialCost?: number; laborCost?: number; processingCost?: number; siteInstallCost?: number;
  };
  calcParts?: any[];
}

interface Estimate {
  projectName: string; generatedAt: string;
  totalItems: number; totalQty: number;
  subtotal: number; vat: number; grandTotal: number;
  items: FurnitureItem[];
  categoryBreakdown: { category: string; label: string; itemCount: number; totalQty: number; totalAmount: number }[];
  costSummary?: {
    totalManufacturing: number; totalPackaging: number; totalDelivery: number;
    totalInstallation: number; totalOverhead: number; totalProfit: number;
    totalMaterial?: number; totalLabor?: number; totalProcessing?: number;
    totalTransport?: number; totalOtherExpense?: number; totalFlameRetardant?: number;
    totalMinorTransport?: number; totalSetting?: number; totalSiteInstall?: number; totalWasteDisposal?: number;
  };
  priceGrade: string;
  estimateType: string;
  warnings: string[];
}

interface PriceModel {
  category: string; label: string; count: number; totalAmount: number;
  pricePerM2: { min: number; avg: number; max: number }; description: string;
}

/* ── 아이콘 ── */
const Icons = {
  furniture: <Box size={20} strokeWidth={2} />,
  add: <PlusCircle size={16} strokeWidth={2} />,
  download: <Download size={14} strokeWidth={2} />,
  nas: <HardDrive size={16} strokeWidth={2} className="inline-block mr-1" />,
};

const readApiError = async (res: Response, fallback: string) => {
  const raw = await res.text();
  if (!raw) return `${fallback} (HTTP ${res.status})`;

  try {
    const body = JSON.parse(raw);
    const message =
      typeof body.error === 'string'
        ? body.error
        : body.error?.message || body.message || fallback;
    const hint = body.diagnostics?.hint ? `\n힌트: ${body.diagnostics.hint}` : '';
    const firstAttempt = body.diagnostics?.firstAttempt?.error
      ? `\n1차 분석: ${body.diagnostics.firstAttempt.error}`
      : '';
    const retryAttempt = body.diagnostics?.retryAttempt?.error
      ? `\n재시도: ${body.diagnostics.retryAttempt.error}`
      : '';
    return `${message}${hint}${firstAttempt}${retryAttempt}`;
  } catch {
    return `${fallback} (HTTP ${res.status}): ${raw.slice(0, 240)}`;
  }
};

/* ── 샘플 데이터: A 프로젝트 ── */
const DEFAULT_SAMPLE = [
  { name: '안내데스크', code: 'FU-00', w: 3435, d: 950, h: 1000, qty: 1 },
  { name: '업무데스크', code: 'FU-01', w: 3750, d: 3000, h: 1000, qty: 1 },
  { name: '플랜트 박스', code: 'FU-02', w: 2300, d: 350, h: 230, qty: 1 },
  { name: '벽면서가_A', code: 'FU-03', w: 800, d: 350, h: 3300, qty: 8 },
  { name: '벽면서가_B', code: 'FU-03', w: 775, d: 350, h: 1065, qty: 5 },
  { name: '전시용서가_A', code: 'FU-04', w: 1180, d: 150, h: 3300, qty: 3 },
  { name: '전시용서가_B', code: 'FU-04', w: 1270, d: 150, h: 3300, qty: 2 },
  { name: '어린이 벽면서가_A', code: 'FU-07', w: 710, d: 700, h: 3300, qty: 10 },
  { name: '어린이 벽면서가_D', code: 'FU-08', w: 930, d: 700, h: 3300, qty: 5 },
  { name: '일반자료실_벽면서가_A', code: 'FU-20', w: 665, d: 400, h: 2700, qty: 3 },
  { name: '일반자료실_리딩누크_A', code: 'FU-31', w: 1540, d: 1100, h: 370, qty: 3 },
  { name: '북스텝 그리드루버_A', code: 'FU-32', w: 1420, d: 150, h: 1065, qty: 1 },
  { name: '자율학습실_열람테이블_A', code: 'FU-25', w: 1350, d: 400, h: 750, qty: 1 },
  { name: '시청각실 벽면 수납장_A', code: 'FU-30', w: 600, d: 250, h: 850, qty: 7 },
  { name: '홀 벽면서가_A', code: 'FU-33', w: 570, d: 350, h: 2700, qty: 17 },
];

/* ── 메인 컴포넌트 ── */
export default function FurnitureAgentPage() {
  const [tab, setTab] = useState<'estimate' | 'nas' | 'bulk' | 'models' | 'archive' | 'materials'>('estimate');
  const [items, setItems] = useState<Array<typeof DEFAULT_SAMPLE[0] & {category?: string; thickness?: number; hasHardware?: boolean; hasSocket?: boolean; hasLighting?: boolean; hasCushion?: boolean; shelfCount?: number | null; location?: string; ledM?: number; cushionM2?: number; socketCount?: number; finishMaterial?: string; priceGrade?: string;}>>([]);
  const [projectName, setProjectName] = useState('');
  const [recipient, setRecipient] = useState('');
  const [priceGrade, setPriceGrade] = useState<'min' | 'avg' | 'high' | 'max'>('avg');
  const [estimateType, setEstimateType] = useState<'designinsight' | 'ssot' | 'narajangteo' | 'compare_3'>('designinsight');
  const [sourceType, setSourceType] = useState<'dxf'|'skp'|'photo'>('dxf');
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [models, setModels] = useState<PriceModel[]>([]);
  
  // ── DB & NAS Hybrid Archive ──
  const [savedEstimates, setSavedEstimates] = useState<any[]>([]);

  const fetchArchives = useCallback(async () => {
    try {
      const res = await fetch('/api/furniture-agent/archive');
      const data = await res.json();
      if (data.success && data.archives) {
        setSavedEstimates(data.archives);
      }
    } catch (e) { console.error('Failed to load archives', e); }
  }, []);

  useEffect(() => {
    fetchArchives();
    const savedProjectName = localStorage.getItem('snapgrid_projectName');
    const savedRecipient = localStorage.getItem('snapgrid_recipient');
    if (savedProjectName) setProjectName(savedProjectName);
    if (savedRecipient) setRecipient(savedRecipient);
  }, [fetchArchives]);

  useEffect(() => {
    if (projectName) localStorage.setItem('snapgrid_projectName', projectName);
  }, [projectName]);

  useEffect(() => {
    if (recipient) localStorage.setItem('snapgrid_recipient', recipient);
  }, [recipient]);

  const saveToArchive = useCallback(async () => {
    if (!estimate) return;
    
    const isVersion = confirm(`"${projectName}" 견적서를 DB(메타데이터)와 NAS(상세데이터)에 분산 저장하시겠습니까?\n(기존 문서 덮어쓰기가 아닌 새 버전으로 안전하게 보관됩니다.)`);
    if (!isVersion) return;

    const workspaceData = {
      name: projectName,
      itemsCount: items.length,
      total: estimate.grandTotal,
      type: estimateType === 'narajangteo' ? '조달청 공고' : estimateType === 'compare_3' ? '비교견적' : '기본설계',
      estimateData: estimate, 
      workspaceItems: items,
      priceGrade,
      estimateType
    };
    
    try {
      const res = await fetch('/api/furniture-agent/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectName,
          title: workspaceData.type,
          totalPrice: estimate.grandTotal,
          itemCount: items.length,
          workspaceData
        })
      });
      const data = await res.json();
      if (data.success) {
        alert(`NAS와 DB에 하이브리드 저장 완료!\n경로: ${data.archive.nasJsonPath}`);
        fetchArchives();
        setTab('archive');
      } else {
        alert('저장 실패: ' + data.error);
      }
    } catch(e) {
      alert('저장 중 통신 오류가 발생했습니다.');
    }
  }, [estimate, projectName, items, priceGrade, estimateType, fetchArchives]);

  const deleteFromArchive = useCallback(async (id: string) => {
    if(!confirm('정말 삭제하시겠습니까? (DB에서만 삭제되며 NAS 파일은 유지됩니다)')) return;
    try {
      await fetch(`/api/furniture-agent/archive?id=${id}`, { method: 'DELETE' });
      fetchArchives();
    } catch(e) { alert('삭제 오류'); }
  }, [fetchArchives]);

  const loadFromArchive = useCallback(async (metadata: any) => {
    alert(`[NAS 연동 알림]\nNAS에 저장된 원본 상세 데이터와 연관 도면/PDF 경로를 불러옵니다.\n경로: ${metadata.nasJsonPath}`);
    setLoading(true);
    try {
      const res = await fetch(`/api/furniture-agent/archive?id=${metadata.id}`);
      const data = await res.json();
      if (data.success && data.data) {
        const doc = data.data;
        setProjectName(doc.name || metadata.projectName || '불러온 견적');
        setItems(doc.workspaceItems || []);
        setPriceGrade(doc.priceGrade || 'avg');
        setEstimateType(doc.estimateType || 'designinsight');
        setEstimate(doc.estimateData || null);
        setNasPath(metadata.nasJsonPath);
        setTab('estimate');
      } else {
        alert('문서 상세 데이터를 NAS에서 불러오지 못했습니다.');
      }
    } catch(e) {
      alert('불러오기 오류');
    } finally {
      setLoading(false);
    }
  }, []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [nasPath, setNasPath] = useState('');
  const [selectedItemIdx, setSelectedItemIdx] = useState<number | null>(null);
  const [expandedItemNos, setExpandedItemNos] = useState<Set<number>>(new Set());
  const [expandedInputNos, setExpandedInputNos] = useState<Set<number>>(new Set());
  const [hoveredParts, setHoveredParts] = useState<Record<number, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  // ── NEW: Local folder & Omni-chat ──
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [omniChatMessage, setOmniChatMessage] = useState('');
  const [omniChatHistory, setOmniChatHistory] = useState<Array<{role: 'user' | 'assistant', content: any}>>([]);
  const [masterPlan, setMasterPlan] = useState<any[]>([]);
  const [_pendingItems, setPendingItems] = useState<any[]>([]);
  const [omniChatLoading, setOmniChatLoading] = useState(false);
  const [showOmniChat, setShowOmniChat] = useState(true);
  const [rightPanelWidth, setRightPanelWidth] = useState(380);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const previewImageInputRef = useRef<HTMLInputElement>(null);
  const pdfOneShotInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  // ── NEW: PDF Options Modal ──
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfOptions, setPdfOptions] = useState({ includeSummary: true, includeDetails: true, author: '디자인인사이트(주)', reviewer: '황지혜', checker: '박현우', approver: '배경철' });

  // ── NEW: Engine Progress Stream ──
  const [engineProgress, setEngineProgress] = useState<{ active: boolean; step: string; current: number; total: number; msg: string }>({ active: false, step: '', current: 0, total: 0, msg: '' });

  // ── NEW: Job Stream Observability ──
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const { job, actionLog } = useJobStream(activeJobId);

  const [showRatios, setShowRatios] = useState(false);
  const [showCostDetails, setShowCostDetails] = useState(false);
  const [customRatios, setCustomRatios] = useState({
    rawMaterial: 0.20, cutting: 0.05, edge: 0.05,
    assembly: 0.13, painting: 0.05, outsourcing: 0.10, packaging: 0.02,
    generalMgmt: 0.05,
    transport: 0.05, loading: 0.02, settingLabor: 0.05,
    miscExpense: 0.05, profit: 0.06
  });

  const resetWorkspace = useCallback(() => {
    setItems([]);
    setProjectName('');
    setRecipient('');
    setEstimate(null);
    setOmniChatHistory([]);
    setOmniChatMessage('');
    setLocalFiles([]);
    setUploadedFile(null);
    setNasPath('');
    setPreviewImage(null);
    setError('');
  }, []);

  // ── 샘플 로드 ──
  const loadSample = useCallback(() => {
    setItems([...DEFAULT_SAMPLE]);
    setProjectName('A 프로젝트 인테리어');
  }, []);

  // ── 아이템 추가 ──
  const addItem = useCallback(() => {
    setItems(prev => [...prev, { name: '', code: '', w: 0, d: 0, h: 0, qty: 1 }]);
  }, []);

  const removeItem = useCallback((idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateItem = useCallback((idx: number, field: string, value: any) => {
    setItems(prev => {
      const copy = [...prev];
      (copy[idx] as any)[field] = value;
      return copy;
    });
  }, []);

  const moveItemUp = useCallback((idx: number) => {
    if (idx === 0) return;
    setItems(prev => {
      const copy = [...prev];
      [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
      return copy;
    });
    setSelectedItemIdx(prev => prev === idx ? idx - 1 : (prev === idx - 1 ? idx : prev));
  }, []);

  const moveItemDown = useCallback((idx: number) => {
    setItems(prev => {
      if (idx === prev.length - 1) return prev;
      const copy = [...prev];
      [copy[idx + 1], copy[idx]] = [copy[idx], copy[idx + 1]];
      return copy;
    });
    setSelectedItemIdx(prev => prev === idx ? idx + 1 : (prev === idx + 1 ? idx : prev));
  }, []);

  // ── 견적서 생성 ──
  const generateEstimate = useCallback(async (overrideItems?: any[]) => {
    const targetItems = Array.isArray(overrideItems) ? overrideItems : items;
    if (targetItems.length === 0) return setError('가구 아이템을 추가하세요');
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/furniture-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'estimate', projectName, items: targetItems, priceGrade, estimateType, customRatios, recipient }),
      });
      const data = await res.json();
      if (data.success) {
        setEstimate(data.estimate);
        setTab('estimate');
      } else {
        setError(data.error || '견적 생성 실패');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [items, projectName, priceGrade, estimateType, customRatios, recipient]);

  const updateEstimateItem = useCallback(async (no: number, action: 'delete' | 'inc' | 'dec') => {
    const idx = no - 1;
    let newItems = [...items];
    if (action === 'delete') {
      newItems.splice(idx, 1);
    } else if (action === 'inc') {
      newItems[idx].qty++;
    } else if (action === 'dec') {
      if (newItems[idx].qty > 1) {
        newItems[idx].qty--;
      } else {
        return;
      }
    }
    setItems(newItems);
    generateEstimate(newItems);
  }, [items, generateEstimate]);

  // ── NAS 도면 → 견적서 ──
  const generateFromNas = useCallback(async () => {
    if (!nasPath.trim()) return setError('NAS 경로를 입력하세요');
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/furniture-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'parse-nas', nasPath, projectName, priceGrade, estimateType, sourceType }),
      });
      const data = await res.json();
      if (data.success) {
        setItems(data.estimate.items.map((i: any) => ({
          name: i.name, code: i.code || '', location: (i as any).location || '',
          w: i.w, d: i.d, h: i.h, qty: i.qty,
          category: i.category,
          finishMaterial: (i as any).finishMaterial || 'LPM',
          thickness: i.thickness || 18,
          hasHardware: !!i.hasHardware, hasSocket: !!i.hasSocket,
          hasLighting: !!i.hasLighting, hasCushion: !!i.hasCushion,
          shelfCount: (i as any).shelfCount,
          openRatio: parseFloat(String(i.openRatio || '0')) || 0,
          calcParts: i.calcParts || [],
          moduleBreakdown: i.moduleBreakdown || [],
          priceGrade: i.priceGrade || priceGrade,
        })));
        setEstimate(data.estimate);
        setTab('estimate');
      } else {
        setError(data.error || '도면 파싱 실패');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [nasPath, projectName, priceGrade, estimateType, sourceType]);

  // ── 단가 모델 로드 ──
  const loadModels = useCallback(async () => {
    try {
      const res = await fetch('/api/furniture-agent?action=price-models', { cache: 'no-store' });
      const data = await res.json();
      if (data.success) {
        setModels(data.models);
        // 글로벌 참조를 위해 window 단위에 바인딩
        (window as any)._furnitureModels = data.models;
      }
    } catch {}
  }, []);

  const getPriceModel = (cat: string) => {
    const arr = (window as any)._furnitureModels || models;
    return arr.find((m: any) => m.category === cat) || arr[0] || { pricePerM2: { min: 0, avg: 0, max: 0 } };
  };

  // ── Excel 업로드 파싱 ──
  const handleExcelUpload = useCallback(async (file: File) => {
    setLoading(true);
    setError('');
    try {
      const buffer = await file.arrayBuffer();
      // 대용량 파일에서 String.fromCharCode(...) 스프레드는 콜스택 초과 — 32KB 청크로 인코딩
      const bytes = new Uint8Array(buffer);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
      }
      const base64 = btoa(binary);
      const res = await fetch('/api/furniture-agent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'parse-excel', excelBase64: base64, fileName: file.name }),
      });
      const data = await res.json();
      if (data.success) {
        setItems(data.items.map((i: any) => ({
          name: i.name, code: i.code, w: i.w, d: i.d, h: i.h, qty: i.qty,
          shelfCount: i.shelfCount, ledM: i.ledM, cushionM2: i.cushionM2, socketCount: i.socketCount,
          hasLighting: !!i.hasLighting, hasCushion: !!i.hasCushion, hasSocket: !!i.hasSocket, hasHardware: !!i.hasHardware,
          finishMaterial: i.finishMaterial, priceGrade: i.priceGrade,
        })));
        setProjectName(file.name.replace(/\.[^.]+$/, ''));
      } else {
        setError(data.error || '파싱 실패');
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── NEW: SSE Pipeline for Gemini 2.5 Vision ──
  const analyzeBlueprintSSE = async (file: File) => {
    if (!file.name.match(/\.(pdf|jpg|jpeg|png)$/i)) {
      setError('비전 파싱은 PDF 또는 이미지 파일만 지원합니다.'); return;
    }
    setLoading(true); setOmniChatLoading(true);
    setOmniChatHistory(prev => [...prev, { role: 'user', content: `[비전 정밀 파싱: ${file.name}]` }]);
    setError('');
    
    try {
      const formData = new FormData(); formData.append('file', file);
      
      const response = await fetch('/api/takeoff/analyze', {
        method: 'POST', body: formData
      });
      
      if (!response.body) throw new Error('ReadableStream not supported');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        
        const chunks = buffer.split('\n\n');
        buffer = chunks.pop() || '';
        
        for (const chunk of chunks) {
          const lines = chunk.split('\n');
          let event = 'message';
          let dataStr = '';
          for (const line of lines) {
            if (line.startsWith('event: ')) event = line.slice(7).trim();
            else if (line.startsWith('data: ')) dataStr = line.slice(6).trim();
          }
          if (dataStr) {
            try {
              const data = JSON.parse(dataStr);
              if (event === 'progress') {
                setOmniChatHistory(prev => {
                   const last = prev[prev.length - 1];
                   if (last && last.role === 'assistant' && typeof last.content === 'string' && last.content.includes('[비전 파싱 진행률]')) {
                      return [...prev.slice(0, -1), { role: 'assistant', content: `[비전 파싱 진행률]\n🟢 Step ${data.step}: ${data.message}` }];
                   }
                   return [...prev, { role: 'assistant', content: `[비전 파싱 진행률]\n🟢 Step ${data.step}: ${data.message}` }];
                });
              } else if (event === 'complete') {
                if (data.isOverview && data.overviewData && data.overviewData.master_plan) {
                  const p = data.overviewData.master_plan;
                  setMasterPlan(prev => [...prev, ...p]);
                  setOmniChatHistory(prev => [...prev, { role: 'assistant', content: `📑 총괄 계획표 발견! ${p.length}개의 가구/공간 배치 정보를 마스터 플랜에 추가했습니다.` }]);
                } else {
                  const arr = data.parsedItems || (data.parsedItem ? [data.parsedItem] : []);
                  setItems(prev => [...prev, ...arr.map((i: any) => ({
                    name: i.name || '집기', code: i.code || '', location: (i as any).location || '', 
                    w: i.w || 1000, d: i.d || 450, h: i.h || 750, qty: i.qty || 1,
                    hasHardware: false, hasSocket: false, hasLighting: !!i.hasLighting, finishMaterial: (i as any).finishMaterial || 'LPM',
                    thickness: i.thickness || 18, hasCurvedShape: !!i.hasCurvedShape, openRatio: i.openRatio || 0, calcParts: i.calcParts || []
                  }))]);
                  if (arr.length > 0) {
                    setOmniChatHistory(prev => [...prev, { role: 'assistant', content: `✅ 비전 파싱 완료. ${arr.length}개 객체가 좌측 표에 추가되었습니다.` }]);
                  }
                }
              } else if (event === 'error') {
                throw new Error(data.message);
              }
            } catch(e){} 
          }
        }
      }
    } catch(e: any) {
      setError(`SSE Error: ${e.message}`);
      setOmniChatHistory(prev => [...prev, { role: 'assistant', content: `❌ 통신 실패: ${e.message}` }]);
    } finally {
      setLoading(false); setOmniChatLoading(false);
    }
  };

  // ── NEW: 도면 특화 이미지 압축 (해상도/치수 보호) ──
  const compressForDrawing = async (file: File): Promise<Blob> => {
    if (!file.type.startsWith('image/')) return file;
    try {
      const img = await createImageBitmap(file);
      // 장변 4096px 제한 (일반 이미지의 2048px보다 크게 설정하여 텍스트 깨짐 방지)
      const scale = Math.min(1, 4096 / Math.max(img.width, img.height));
      if (scale === 1 && file.size < 4 * 1024 * 1024) return file; // 변경점 없으면 원본 패스
      
      const w = Math.round(img.width * scale), h = Math.round(img.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      return new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b || file), 'image/jpeg', 0.92);
      });
    } catch (e) {
      return file; // 변환 실패 시 원본 사용
    }
  };

  // ── NEW: Local folder file selection ──
  const handleLocalFolderSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(f =>
      f.name.match(/\.(dxf|pdf|skp|jpg|jpeg|png)$/i)
    );

    if (validFiles.length === 0) {
      setError('지원하지 않는 파일 형식입니다. DXF, PDF, SKP, JPG, PNG만 가능합니다.');
      return;
    }

    setLocalFiles(validFiles);
    setLoading(true);
    setOmniChatLoading(true);
    setOmniChatHistory(prev => [...prev, { role: 'user', content: `[파일 업로드: ${validFiles[0].name} - 귀가 추출 중...]` }]);
    setError('');

    try {
      const newJobId = `job-agent-${Date.now()}`;
      setActiveJobId(newJobId);
      
      // Process first file as example
      const file = validFiles[0];
      const compressedBlob = await compressForDrawing(file);
      
      const formData = new FormData();
      formData.append('file', compressedBlob, file.name);
      formData.append('action', 'parse-local');
      formData.append('jobId', newJobId);

      const res = await fetch('/api/furniture-agent', {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`통신 실패 상태에러(${res.status}): ${errText.slice(0, 100)}`);
      }

      const parseNum = (val: any, def: number) => {
        if (typeof val === 'number') return val;
        if (!val) return def;
        const num = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
        return isNaN(num) ? def : num;
      };

      const data = await res.json();
      
      const analysisText = data.analysisReport || (data.error && data.error.message ? data.error.message : data.error) || '파일 분석을 완료했습니다.';
      const todosHTML = data.todos ? data.todos.map((t: string) => `✅ ${t}`) : [];
      const qsHTML = data.questions ? data.questions.map((q: string) => `❓ ${q}`) : [];

      if (data.success) {
        setUploadedFile(file);
        
        const newItems = (data.items || data.pendingItems || []).map((i: any) => ({
           name: i.name || '집기', code: i.code || '', location: (i as any).location || '',
           w: parseNum(i.w, 1000), d: parseNum(i.d, 450), h: parseNum(i.h, 750), qty: parseNum(i.qty, 1),
           hasHardware: !!i.hasHardware, hasSocket: !!i.hasSocket,
           hasLighting: !!i.hasLighting, finishMaterial: i.finishMaterial, thickness: i.thickness,
           hasCurvedShape: !!i.hasCurvedShape, openRatio: parseNum(i.openRatio, 0),
           calcParts: (i.calcParts || []).map((cp: any) => ({
             ...cp, w: parseNum(cp.w, 0), d: parseNum(cp.d, 0), h: parseNum(cp.h, 0)
           }))
        }));
        
        if (newItems.length > 0) {
          setPendingItems(newItems);
        } else {
          setItems(prev => [...prev, ...newItems]);
        }
        
        setProjectName(file.name.replace(/\.[^.]+$/, ''));
        setPreviewImage(URL.createObjectURL(file));

        // 원본 도면을 drawingStore에 저장 (표준품셈 PDF에 원본 첨부용)
        if (newItems.length > 0) {
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            const entries = newItems.map((item: any) => ({ code: item.code, dataUrl }));
            putDrawings(entries).catch(e => console.warn('[drawingStore] save failed:', e));
          };
          reader.readAsDataURL(file);
        }

        if (data.estimate) {
          setEstimate(data.estimate);
        }
      } else {
        setError(data.error || '로컬 파일 파싱 실패');
      }

      setOmniChatHistory(prev => [...prev, {
        role: 'assistant',
        content: (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ whiteSpace: 'pre-wrap' }}><strong>[도면 분석 리포트]</strong><br/>{analysisText}</div>
            
            {todosHTML.length > 0 && (
              <div style={{ fontSize: 11, background: 'rgba(0,0,0,0.05)', padding: '8px 10px', borderRadius: 6, lineHeight: 1.6 }}>
                <strong>[ 진행 현황 및 To-Do ]</strong><br/>
                {todosHTML.map((t: string, idx: number) => <div key={idx}>{t}</div>)}
              </div>
            )}
            
            {qsHTML.length > 0 && (
              <div style={{ fontSize: 11, background: 'rgba(239,68,68,0.1)', padding: '8px 10px', borderRadius: 6, color: '#b91c1c', lineHeight: 1.6 }}>
                <strong>[ 추가 확인 (Q&A) ]</strong><br/>
                {qsHTML.map((q: string, idx: number) => <div key={idx}>{q}</div>)}
              </div>
            )}
            
            {data.success && (data.items || data.pendingItems) && (data.items || data.pendingItems).length > 0 && (
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  style={{ padding: '6px 12px', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', borderRadius: 6, fontSize: 11, border: 'none', cursor: 'pointer', boxShadow: '0 2px 6px rgba(16,185,129,0.3)' }}
                  onClick={() => {
                     setItems(prev => [...prev, ...((data.items || data.pendingItems).map((i: any) => ({
                       name: i.name || '집기', code: i.code || '', location: (i as any).location || '',
                       w: parseNum(i.w, 1000), d: parseNum(i.d, 450), h: parseNum(i.h, 750), qty: parseNum(i.qty, 1),
                       hasHardware: !!i.hasHardware, hasSocket: !!i.hasSocket,
                       hasLighting: !!i.hasLighting, finishMaterial: i.finishMaterial, thickness: i.thickness,
                       hasCurvedShape: !!i.hasCurvedShape, openRatio: parseNum(i.openRatio, 0),
                       calcParts: (i.calcParts || []).map((cp: any) => ({
                         ...cp, w: parseNum(cp.w, 0), d: parseNum(cp.d, 0), h: parseNum(cp.h, 0)
                       }))
                     })))]);
                     setPendingItems([]);
                     setOmniChatHistory(h => [...h, { role: 'assistant', content: '의사결정 피드백 반영 완료. 좌측 표에 신규 견적 데이터를 병합했습니다.' }]);
                  }}
                >
                  AI 권장사양 즉시 적용 ({(data.items || data.pendingItems).length}종)
                </button>
              </div>
            )}
          </div>
        )
      }]);
    } catch (e: any) {
      setError(e.message);
      setOmniChatHistory(prev => [...prev, { role: 'assistant', content: `통신 오류 발생: ${e.message}` }]);
    } finally {
      setLoading(false);
      setOmniChatLoading(false);
    }
  }, []);

  // ── Engine SSE Generate Docs ──
  const handleGenerateDocs = async (formats: string[]) => {
    if (!estimate) return;
    setEngineProgress({ active: true, step: 'init', current: 0, total: 0, msg: '작업 준비 중...' });
    
    try {
      const res = await fetch((process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + "/engine/api/quote/batch/stream", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: estimate.items.map((i, idx) => ({ 
            ...i,
            no: i.no || idx + 1,
            name: i.name || "제작가구",
            w: Number(i.w) || 100,
            d: Number(i.d) || 100,
            h: Number(i.h) || 100,
            qty: Number(i.qty) > 0 ? Number(i.qty) : 1,
            up: Number(i.unitPrice) || 0,
            fu: i.code || "" 
          })),
          project: estimate.projectName || "제작가구 견적",
          formats,
          include_diagrams: true
        })
      });
      if (!res.ok) {
        try {
          const errBody = await res.json();
          throw new Error("API 422/500 에러: " + JSON.stringify(errBody.detail || errBody));
        } catch(e) {
          throw new Error(`엔진 통신 에러 (HTTP ${res.status}): ${e instanceof Error ? e.message : '알 수 없음'}`);
        }
      }
      if (!res.body) throw new Error("Stream not supported");
      
      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        buffer += decoder.decode(value, { stream: true });
        let newlineIdx;
        while ((newlineIdx = buffer.indexOf("\n")) >= 0) {
          const line = buffer.slice(0, newlineIdx).trim();
          buffer = buffer.slice(newlineIdx + 1);
          
          if (line.startsWith("data: ") && line.length > 6) {
            try {
              const data = JSON.parse(line.substring(6));
              if (data.type === "progress") {
                setEngineProgress(prev => ({ ...prev, step: data.step, current: data.current || prev.current, total: data.total || prev.total, msg: data.msg || prev.msg }));
              } else if (data.type === "complete") {
                setEngineProgress(prev => ({ ...prev, msg: '생성 완료! 다운로드를 시작합니다.', current: prev.total, step: 'done' }));
                setTimeout(() => setEngineProgress(p => ({ ...p, active: false })), 1500);
                
                const { xlsx_url, pdf_url } = data.result;
                if (formats.includes("xlsx") && xlsx_url) {
                   const a = document.createElement('a');
                   a.href = (process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + xlsx_url;
                   a.download = `${estimate.projectName}_마감산출서.xlsx`;
                   a.click();
                }
                if (formats.includes("pdf") && pdf_url) {
                   const a = document.createElement('a');
                   a.href = (process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + pdf_url;
                   a.download = `${estimate.projectName}_상세견적서.pdf`;
                   a.click();
                }
              } else if (data.type === "error") {
                throw new Error(data.msg);
              }
            } catch(e: any) {
              if (e instanceof Error && !e.message.includes("Unexpected end of JSON input")) {
                alert("엔진 오류: " + e.message);
                setEngineProgress(p => ({ ...p, active: false }));
              }
            }
          }
        }
      }
    } catch(err: any) {
      alert("엔진 통신 에러: " + (err.message || String(err)));
      setEngineProgress(p => ({ ...p, active: false }));
    }
  };

  // ── NEW: Omni-chat collaboration ──
  const handlePaste = useCallback(async (e: React.ClipboardEvent) => {
    const itemsData = e.clipboardData?.items;
    if (!itemsData) return;

    let imageFile: File | null = null;
    for (let i = 0; i < itemsData.length; i++) {
      if (itemsData[i].type.indexOf('image') === 0) {
        imageFile = itemsData[i].getAsFile();
        break;
      }
    }

    if (imageFile) {
      // Prevent default paste if we found an image
      e.preventDefault();
      setUploadedFile(imageFile);
      setOmniChatLoading(true);
      
      const pseudoJobId = "job-" + Date.now().toString(36);
      setActiveJobId(pseudoJobId);

      setOmniChatHistory(prev => [...prev, { role: 'user', content: '[도면 캡처 이미지 첨부 · 분석 시작]' }]);
      
      const formData = new FormData();
      formData.append('file', imageFile);
      formData.append('action', 'parse-local');
      formData.append('jobId', pseudoJobId);

      try {
        // 🛡️ File/Blob 가드: _sseDrawingFile 검증
        const drawingFile = formData.get('file') as unknown;
        if (!drawingFile || (!(drawingFile instanceof File) && !(drawingFile instanceof Blob))) {
          throw new Error(`도면 파일이 유효하지 않습니다 (type: ${drawingFile?.constructor?.name || 'null'}). 이미지를 다시 첨부해주세요.`);
        }
        const res = await fetch('/api/furniture-agent', {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          throw new Error(await readApiError(res, '도면 분석에 실패했습니다'));
        }

        const data = await res.json();
        
        // 1. 도면 데이터가 있든 없든 AI의 추론 리포트부터 먼저 띄웁니다.
        const analysisText = data.analysisReport || (data.error && data.error.message ? data.error.message : data.error) || '이미지 분석을 완료했습니다.';
        const todosHTML = data.todos ? data.todos.map((t: string) => `✅ ${t}`) : [];
        const qsHTML = data.questions ? data.questions.map((q: string) => `❓ ${q}`) : [];
        
        const parseNum = (val: any, def: number) => {
          if (typeof val === 'number') return val;
          if (!val) return def;
          const num = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
          return isNaN(num) ? def : num;
        };

        let newItems: any[] = [];
        if (data.success && data.pendingItems && data.pendingItems.length > 0) {
          newItems = data.pendingItems.map((i: any) => ({
             name: i.name || '집기', code: i.code || '', location: (i as any).location || '',
             w: parseNum(i.w, 1000), d: parseNum(i.d, 450), h: parseNum(i.h, 750), qty: parseNum(i.qty, 1),
             hasHardware: !!i.hasHardware, hasSocket: !!i.hasSocket,
             hasLighting: !!i.hasLighting, finishMaterial: i.finishMaterial, thickness: i.thickness,
             hasCurvedShape: !!i.hasCurvedShape, openRatio: parseNum(i.openRatio, 0),
             calcParts: (i.calcParts || []).map((cp: any) => ({
               ...cp, w: parseNum(cp.w, 0), d: parseNum(cp.d, 0), h: parseNum(cp.h, 0)
             }))
          }));
          setPendingItems(newItems);
          setPreviewImage(URL.createObjectURL(imageFile));
        }

        setOmniChatHistory(prev => [...prev, {
          role: 'assistant',
          content: (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ whiteSpace: 'pre-wrap' }}><strong>[도면 분석 리포트]</strong><br/>{analysisText}</div>
              
              {todosHTML.length > 0 && (
                <div style={{ fontSize: 11, background: 'rgba(0,0,0,0.05)', padding: '8px 10px', borderRadius: 6, lineHeight: 1.6 }}>
                  <strong>[ 진행 현황 및 To-Do ]</strong><br/>
                  {todosHTML.map((t: string, idx: number) => <div key={idx}>{t}</div>)}
                </div>
              )}
              
              {qsHTML.length > 0 && (
                <div style={{ fontSize: 11, background: 'rgba(239,68,68,0.1)', padding: '8px 10px', borderRadius: 6, color: '#b91c1c', lineHeight: 1.6 }}>
                  <strong>[ 추가 확인 (Q&A) ]</strong><br/>
                  {qsHTML.map((q: string, idx: number) => <div key={idx}>{q}</div>)}
                </div>
              )}
              
              {newItems.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      setItems(prevItems => [...prevItems, ...newItems]);
                      setPendingItems([]);
                      setOmniChatHistory(h => [...h, { 
                        role: 'assistant', 
                        content: '의사결정 확정 완료. 견적서 산출 엔진(겉넓이/수직Bay 분해)을 가동하여 데이터를 표에 병합했습니다.' 
                      }]);
                    }}
                    style={{ padding: '8px 14px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                    확인 및 견적 엔진 가동
                  </button>
                </div>
              )}
            </div>
          )
        }]);
      } catch (err: any) {
        alert(`🚨 [도면 인식 실패]\n분석 서버는 응답했지만, 업로드한 이미지에서 견적 가능한 가구 도면 정보를 추출하지 못했습니다.\n\n권장 조치:\n1. 더 선명한 원본 캡처 또는 PDF로 다시 업로드\n2. 가구 상세도/입면도처럼 치수선이 보이는 영역만 잘라 업로드\n3. 화면 캡처라면 확대 후 재캡처\n\n상세:\n${err?.message || String(err)}`);
        setOmniChatHistory(prev => [...prev, {
          role: 'assistant',
          content: `도면 인식 실패: ${err?.message || String(err)}`
        }]);
      } finally {
        setOmniChatLoading(false);
      }
    }
  }, []);

  const handleOmniChatSubmit = useCallback(async () => {
    if (!omniChatMessage.trim()) return;

    const userMessage = omniChatMessage;
    setOmniChatMessage('');
    setOmniChatHistory(prev => [...prev, { role: 'user', content: userMessage }]);
    setOmniChatLoading(true);

    try {
      // 🚀 1. 도면 파일이 업로드된 상태에서의 대화는 무조건 '비전 피드백' 흐름으로 취급하여 재추출 가동
      // 단, 대화가 순수 일반 질문인 경우는 제외 (간결화를 위해 일단 도면 세션 중엔 모두 피드백으로 간주)
      const isImageSession = !!uploadedFile;
      
      if (isImageSession) {
        setOmniChatHistory(prev => [...prev, {
          role: 'assistant',
          content: '🔄 사용자의 피드백을 인지했습니다. 원본 도면을 바탕으로 맥락을 반영하여 데이터를 재추출/재보정하고 있습니다. 잠시만 기다려주세요...'
        }]);

        // 대화 히스토리 전체를 피드백(feedback)으로 모아서 전송 (과거 맥락 포함)
        const recentHistory = omniChatHistory.map(h => `${h.role === 'user' ? '사용자' : 'AI'}: ${typeof h.content === 'string' ? h.content : '[이미지/표 컴포넌트]'}`).join('\n');
        const totalFeedback = `${recentHistory}\n사용자: ${userMessage}`;

        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('action', 'parse-local');
        formData.append('feedback', totalFeedback);

        const apiRes = await fetch('/api/furniture-agent', {
          method: 'POST',
          body: formData
        });

        if (!apiRes.ok) throw new Error(`업로드 통신 에러(${apiRes.status})`);
        
        const data = await apiRes.json();
        
        const analysisText = data.analysisReport || (data.error && data.error.message ? data.error.message : data.error) || '피드백을 반영한 재추출을 완료했습니다.';
        const todosHTML = data.todos ? data.todos.map((t: string) => `✅ ${t}`) : [];
        const qsHTML = data.questions ? data.questions.map((q: string) => `❓ ${q}`) : [];
        
        const parseNum = (val: any, def: number) => {
          if (typeof val === 'number') return val;
          if (!val) return def;
          const num = parseFloat(String(val).replace(/[^0-9.-]/g, ''));
          return isNaN(num) ? def : num;
        };

        if (data.success && data.pendingItems && data.pendingItems.length > 0) {
          const newItems = data.pendingItems.map((i: any) => ({
             name: i.name || '집기', code: i.code || '', location: (i as any).location || '',
             w: parseNum(i.w, 1000), d: parseNum(i.d, 450), h: parseNum(i.h, 750), qty: parseNum(i.qty, 1),
             hasHardware: !!i.hasHardware, hasSocket: !!i.hasSocket,
             hasLighting: !!i.hasLighting, finishMaterial: i.finishMaterial, thickness: i.thickness,
             hasCurvedShape: !!i.hasCurvedShape, openRatio: parseNum(i.openRatio, 0),
             calcParts: (i.calcParts || []).map((cp: any) => ({
               ...cp, w: parseNum(cp.w, 0), d: parseNum(cp.d, 0), h: parseNum(cp.h, 0)
             }))
          }));
          setPendingItems(newItems);
          
          setOmniChatHistory(prev => [...prev, {
            role: 'assistant',
            content: (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ whiteSpace: 'pre-wrap' }}><strong>[피드백 반영 리포트]</strong><br/>{analysisText}</div>
                {todosHTML.length > 0 && (
                  <div style={{ fontSize: 11, background: 'rgba(0,0,0,0.05)', padding: '8px 10px', borderRadius: 6, lineHeight: 1.6 }}>{todosHTML.map((t:string, idx:number) => <div key={idx}>{t}</div>)}</div>
                )}
                {qsHTML.length > 0 && (
                  <div style={{ fontSize: 11, background: 'rgba(239,68,68,0.1)', padding: '8px 10px', borderRadius: 6, color: '#b91c1c', lineHeight: 1.6 }}>{qsHTML.map((q:string, idx:number) => <div key={idx}>{q}</div>)}</div>
                )}
                <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                  <button 
                    onClick={() => {
                      setItems(prevItems => [...prevItems, ...newItems]);
                      setPendingItems([]);
                      setOmniChatHistory(h => [...h, { role: 'assistant', content: '의사결정 피드백 반영 완료. 좌측 표에 신규 견적 데이터를 병합했습니다.' }]);
                    }}
                    style={{ padding: '8px 14px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 'bold' }}>
                    피드백 결과 엔진 가동
                  </button>
                </div>
              </div>
            )
          }]);
        } else {
          setOmniChatHistory(prev => [...prev, { role: 'assistant', content: `피드백 반영 실패: ${data.error || '추출된 데이터가 없습니다.'}` }]);
        }
        return;
      }

      // 🚀 2. 대기 중인 산출 데이터가 있을 때 "엔진 가동" 등의 자연어로 요청 시 (버튼 클릭 대체)
      if ((userMessage.includes('엔진') || userMessage.includes('가동') || userMessage.includes('승인') || userMessage.includes('적용해')) && _pendingItems.length > 0) {
        setItems(prev => [...prev, ..._pendingItems]);
        setPendingItems([]);
        setOmniChatHistory(prev => [...prev, {
          role: 'assistant',
          content: '✅ AI 추천 의사결정 방식대로 승인했습니다. 즉시 견적 산출 엔진(모듈 자동 분할/자재 산출)을 가동하여 데이터를 표에 병합 완료했습니다! 좌측 견적표를 확인해주세요.'
        }]);
        return;
      }

      // 3. 일반 RAG 대화 처리 (의사결정 버튼도 없고, 재가동도 아닌 경우)

      const context = estimate
        ? `현재 견적 프로젝트: ${estimate.projectName}, 총 ${estimate.totalItems}종 ${estimate.totalQty}개, 합계 ${fmt(estimate.grandTotal)}원\n\n사용자 질문: ${userMessage}`
        : `사용자 질문: ${userMessage}`;

      const res = await fetch('/api/omni-chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [
            { role: 'user', content: context }
          ],
          sessionId: 'furniture-agent',
          tenantId: 'desinsight',
        }),
      });

      if (!res.ok) {
         throw new Error(`API 오류: ${res.status}`);
      }

      const data = await res.json();
      setOmniChatHistory(prev => [...prev, {
        role: 'assistant',
        content: data.answer || data.text || '응답을 생성할 수 없습니다.'
      }]);
    } catch (e: any) {
      alert(`🚨 [통신 타임아웃/오류]\n응답이 지연되거나 실패했습니다.\n다시 시도해주세요.\n\n상세: ${e?.message || String(e)}`);
      setOmniChatHistory(prev => [...prev, {
        role: 'assistant',
        content: `오류: 통신 실패 (${e?.message || String(e)})`
      }]);
    } finally {
      setOmniChatLoading(false);
    }
  }, [omniChatMessage, estimate, _pendingItems]);

  // ── Quick omni-chat prompts ──
  const quickPrompts = [
    { label: '견적 최적화', text: '현재 견적서에서 비용을 10% 절감할 수 있는 방안을 제시해주세요.' },
    { label: '자재 추천', text: '이 가구 프로젝트에 적합한 자재와 마감 옵션을 추천해주세요.' },
    { label: '공기 분석', text: '이 프로젝트의 예상 제작 공기와 설치 일정을 분석해주세요.' },
    { label: '대안 비교', text: '시중 제품과 제작 가구의 가격/품질 비교를 해주세요.' },
  ];

  // ── 견적서 CSV 다운로드 ──
  const downloadCSV = useCallback(() => {
    if (!estimate) return;
    const bom = '\uFEFF';
    let csv = bom;
    
    if (estimate.estimateType === 'compare_3') {
      csv += '제작가구 시중단가 3사 비교견적서\n';
      csv += `현장명:,${estimate.projectName}\n`;
      csv += `생성일:,${new Date(estimate.generatedAt).toLocaleDateString('ko-KR')}\n\n`;
      csv += 'NO,제품명,W,D,H,수량,A사(최소가),B사(평균가),C사(최고가),비고\n';
      estimate.items.forEach(i => {
        const minP = Math.round(i.surfaceM2 * getPriceModel(i.category).pricePerM2.min / 10000) * 10000;
        const avgP = Math.round(i.surfaceM2 * getPriceModel(i.category).pricePerM2.avg / 10000) * 10000;
        const maxP = Math.round(i.surfaceM2 * getPriceModel(i.category).pricePerM2.max / 10000) * 10000;
        csv += `${i.no},"${i.name}",${i.w},${i.d},${i.h},${i.qty},${minP},${avgP},${maxP},${i.category}\n`;
      });
    } else {
      csv += estimate.estimateType === 'narajangteo' ? '나라장터 등록물품 견적서\n' : '제작가구 전문견적서\n';
      csv += `현장명:,${estimate.projectName}\n`;
      csv += `생성일:,${new Date(estimate.generatedAt).toLocaleDateString('ko-KR')}\n\n`;
      csv += 'NO,제품명,코드,W(mm),D(mm),H(mm),수량,단가,원가,자재비(원자재/재단/엣지),제작비(조립/도장/외주/포장),일반관리비,설치비,이윤,산출근거\n';
      estimate.items.forEach(i => {
        const brk = i.costBreakdown || ({} as any);
        csv += `${i.no},"${i.name}",${i.code},${i.w},${i.d},${i.h},${i.qty},${i.unitPrice},${brk.totalOriginCost || 0},${brk.materialTotal || 0},${brk.manufacturingTotal || 0},${brk.generalMgmtCost || 0},${brk.installTotal || 0},${brk.profitCost || 0},"${i.basis}"\n`;
      });
      csv += `\n,,,,,,,,,,,,,,\n`;
      csv += `,,,,,,공급가액,,${estimate.subtotal}\n`;
      csv += `,,,,,,부가가치세,,${estimate.vat}\n`;
      csv += `,,,,,,합계금액,,${estimate.grandTotal}\n`;
    }

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${estimate.projectName}_견적서_${estimate.estimateType}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [estimate]);

  // 초기 모델 로드
  React.useEffect(() => { loadModels(); }, [loadModels]);

  const fmt = (n: number) => n.toLocaleString('ko-KR');

  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // ── 모바일 감지 ──
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ── 테마별 색상 헬퍼 ──
  const colors = {
    // 배경
    bg: isDark ? '#0a0a0f' : '#f8fafc',
    surface: isDark ? '#12121a' : '#ffffff',
    surfaceAlt: isDark ? '#1a1a2e' : '#f1f5f9',
    card: isDark ? '#16162a' : '#ffffff',

    // 텍스트
    text: isDark ? '#f1f5f9' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#475569',
    textMuted: isDark ? '#64748b' : '#94a3b8',

    // 보더
    border: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
    borderLight: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    borderMedium: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',

    // 인풋
    inputBg: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
    inputBorder: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
    inputText: isDark ? '#e2e8f0' : '#1e293b',
    inputPlaceholder: isDark ? '#64748b' : '#94a3b8',

    // 버튼 (오렌지/레드)
    primaryBg: isDark ? 'linear-gradient(135deg, #f97316, #ef4444)' : 'linear-gradient(135deg, #ea580c, #dc2626)',
    primaryBgHover: isDark ? 'linear-gradient(135deg, #fb923c, #f87171)' : 'linear-gradient(135deg, #f97316, #ef4444)',
    primaryBorder: isDark ? 'rgba(249,115,22,0.3)' : 'rgba(234,88,12,0.3)',
    primaryBgLight: isDark ? 'rgba(249,115,22,0.1)' : 'rgba(234,88,12,0.08)',
    primaryText: isDark ? '#f97316' : '#ea580c',

    // 버튼 (그린)
    successBg: isDark ? 'rgba(34,197,94,0.1)' : 'rgba(22,163,74,0.08)',
    successBorder: isDark ? 'rgba(34,197,94,0.3)' : 'rgba(22,163,74,0.3)',
    successText: isDark ? '#4ade80' : '#16a34a',

    // 사이드 패널 (그레이)
    omniBg: isDark ? 'rgba(255,255,255,0.03)' : '#f8fafc',
    omniBorder: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
    omniText: isDark ? '#e2e8f0' : '#475569',
    omniBtnBg: isDark ? 'rgba(255,255,255,0.05)' : '#ffffff',

    // 테이블
    tableHeaderBg: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
    tableBorder: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
    tableHeaderText: isDark ? '#94a3b8' : '#64748b',
    tableRowText: isDark ? '#64748b' : '#64748b',
    tableRowMain: isDark ? '#e2e8f0' : '#1e293b',

    // 에러/경고
    errorBg: isDark ? 'rgba(239,68,68,0.1)' : 'rgba(220,38,38,0.08)',
    errorBorder: isDark ? 'rgba(239,68,68,0.3)' : 'rgba(220,38,38,0.3)',
    errorText: isDark ? '#fca5a5' : '#dc2626',
    warningBg: isDark ? 'rgba(234,179,8,0.1)' : 'rgba(234,179,8,0.08)',
    warningBorder: isDark ? 'rgba(234,179,8,0.2)' : 'rgba(234,179,8,0.2)',
    warningText: isDark ? '#fbbf24' : '#ca8a04',
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: isMobile ? 56 : 64, background: colors.bg, color: colors.text, fontFamily: "'Inter', 'Pretendard', sans-serif" }}>
      {/* ── 헤더 ── */}
      <header style={{ borderBottom: `1px solid ${colors.borderMedium}`, padding: isMobile ? '10px 12px' : '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: colors.surface }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
          <div style={{ width: isMobile ? 30 : 36, height: isMobile ? 30 : 36, borderRadius: 10, background: colors.primaryBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {Icons.furniture}
          </div>
          <div>
            <h1 style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, margin: 0, color: colors.text }}>Furniture Agent</h1>
            {!isMobile && <p style={{ fontSize: 11, color: colors.textSecondary, margin: 0 }}>제작가구 자동 견적 에이전트</p>}
          </div>
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ href: '/boq-agent', label: 'BOQ Agent' }, { href: '/boq-automation', label: 'BOQ Auto' }, { href: '/', label: 'Home' }].map(l => (
              <a key={l.href} href={l.href} style={{ padding: '6px 14px', borderRadius: 8, border: `1px solid ${colors.borderMedium}`, color: colors.textSecondary, fontSize: 12, textDecoration: 'none', background: colors.surface }}>{l.label}</a>
            ))}
          </div>
        )}
        {isMobile && (
          <a href="/" style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${colors.borderMedium}`, color: colors.textSecondary, fontSize: 11, textDecoration: 'none', background: colors.surface }}>Home</a>
        )}
      </header>

      {/* ── 탭 ── */}
      <nav style={{
        display: 'flex', gap: 4, padding: isMobile ? '8px 12px' : '12px 24px',
        borderBottom: `1px solid ${colors.borderLight}`, background: colors.surface,
        overflowX: 'auto', WebkitOverflowScrolling: 'touch',
        ...(isMobile ? { flexWrap: 'nowrap' as const } : {}),
      }}>
        {[
          { key: 'estimate' as any, label: ' 견적서 생성' },
          { key: 'nas' as any, label: ' NAS 도면 연결' },
          { key: 'bulk' as any, label: isMobile ? '📦 대량견적' : '📦 대량견적 (PDF 자동분석)' },
          { key: 'models' as any, label: ' 단가 모델' },
          { key: 'archive' as any, label: isMobile ? ' 문서함' : ' 문서함 (Archive)' },
          { key: 'materials' as any, label: isMobile ? '📚 자재 가이드' : '📚 자재&마감재 가이드' },
        ].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            style={{
              padding: isMobile ? '6px 12px' : '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontSize: isMobile ? 12 : 13,
              fontWeight: tab === tb.key ? 700 : 400,
              whiteSpace: 'nowrap' as const,
              background: tab === tb.key
                ? (tb.key === 'bulk'
                    ? 'rgba(16,185,129,0.12)'
                    : colors.primaryBgLight)
                : 'transparent',
              color: tab === tb.key
                ? (tb.key === 'bulk' ? '#10b981' : colors.primaryText)
                : colors.textSecondary,
            }}>
            {tb.label}
          </button>
        ))}
      </nav>

      <main style={{
        width: '100%', padding: isMobile ? '12px' : '24px 32px', background: colors.bg,
        display: (tab === 'bulk' || isMobile) ? 'block' : 'grid',
        gridTemplateColumns: (tab === 'bulk' || isMobile) ? undefined : `minmax(0, 1fr) 16px ${rightPanelWidth}px`,
        gap: (tab === 'bulk' || isMobile) ? undefined : 16,
      }}>
        {/* ── 좌측 ── */}
        <div>
          {/* 에러 */}
 {error && <div style={{ padding: '12px 16px', borderRadius: 10, background: colors.errorBg, border: `1px solid ${colors.errorBorder}`, color: colors.errorText, fontSize: 13, marginBottom: 16 }}><XCircle className="w-4 h-4 inline-block" />{error}</div>}

          {/* ── TAB: 대량견적 ── */}
          {tab === 'bulk' && (
            <BulkEstimateTab
              colors={colors}
              isDark={isDark}
              onCombinedEstimate={(bulkItems, bulkProjectName) => {
                // bulk 아이템을 견적서 생성 탭으로 이관
                setItems(bulkItems.map((i: any) => ({
                  name: i.name, code: i.code || '', location: (i as any).location || '',
                  w: i.w, d: i.d, h: i.h, qty: i.qty,
                  finishMaterial: (i as any).finishMaterial || 'LPM',
                  thickness: i.thickness || 18,
                  hasLighting: !!i.hasLighting, hasHardware: false, hasSocket: false,
                  hasCurvedShape: !!i.hasCurvedShape,
                  openRatio: parseFloat(String(i.openRatio || '0')) || 0,
                  calcParts: i.calcParts || [],
                })));
                if (bulkProjectName) setProjectName(bulkProjectName);
                setTab('estimate');
              }}
            />
          )}

          {/* ── TAB: 견적서 생성 ── */}
          {tab === 'estimate' && (
            <div>
              {/* === 좌측 작업 영역: 1. 프로젝트 헤더 === */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: 12, padding: 16, background: isDark ? 'transparent' : '#FFFFFF', border: `1px solid ${colors.borderLight}`, borderRadius: 8, marginBottom: 16 }}>
                <div>
                  <label style={{ fontSize: 11, color: colors.textSecondary }}>프로젝트명</label>
                  <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="프로젝트명"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 14, marginTop: 4 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: colors.textSecondary }}>수신처 (기관명)</label>
                  <input value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="예) 00교육지원청"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 14, marginTop: 4 }} />
                </div>
                <div>
                  <label style={{ fontSize: 11, color: colors.textSecondary }}>단가 등급</label>
                  <select value={priceGrade} onChange={e => setPriceGrade(e.target.value as any)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 14, marginTop: 4 }}>
                    <option value="min">최저가 (저가옵션)</option>
                    <option value="avg">평균가 (기본형)</option>
                    <option value="high">고급형 (브랜드)</option>
                    <option value="max">최고급형 (해외명품)</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: colors.textSecondary }}>견적 포맷</label>
                  <select value={estimateType} onChange={e => setEstimateType(e.target.value as any)}
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 14, marginTop: 4 }}>
                    <option value="designinsight">디자인인사이트</option>
                    <option value="ssot">디자인인사이트 SSOT 원가전개</option>
                    <option value="narajangteo">나라장터 등록가</option>
                    <option value="compare_3">시중 3사 비교단가</option>
                  </select>
                </div>
              </div>

              {/* === 좌측 작업 영역: 2. 설정 바 (액션 / 요율 토글) === */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 12 }}>
                  {/* 좌측 액션 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <button onClick={loadSample} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.primaryBorder}`, background: colors.primaryBgLight, color: colors.primaryText, fontSize: 12, cursor: 'pointer' }}>
                      <Package className="w-4 h-4 inline-block mr-1" />샘플 로드
                    </button>
                    <input ref={fileRef} type="file" accept=".xls,.xlsx" hidden onChange={e => e.target.files?.[0] && handleExcelUpload(e.target.files[0])} />
                    <button onClick={() => fileRef.current?.click()} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.inputBg, color: colors.textSecondary, fontSize: 12, cursor: 'pointer' }}>
                      <FileText className="w-4 h-4 inline-block mr-1" />XLS 업로드
                    </button>
                    <button onClick={resetWorkspace} style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.inputBg, color: colors.textSecondary, fontSize: 12, cursor: 'pointer' }}>
                      <RefreshCw className="w-4 h-4 inline-block mr-1" />초기화
                    </button>
                    <input
                      ref={folderInputRef}
                      type="file"
                      multiple
                      accept=".dxf,.pdf,.skp,.jpg,.jpeg,.png"
                      hidden
                      onChange={e => e.target.files && handleLocalFolderSelect(e.target.files)}
                    />
                    <button
                      onClick={() => folderInputRef.current?.click()}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.successBorder}`, background: colors.successBg, color: colors.successText, fontSize: 12, cursor: 'pointer' }}
                    >
                      <FolderOpen className="w-4 h-4 inline-block mr-1" />로컬 파일 업로드
                    </button>
                    <input
                      ref={(el: any) => { (window as any)._sseFileRef = el; }}
                      onChange={e => {
                        const file = e.target.files?.[0];
                        if (file && file instanceof File) {
                          (window as any)._sseDrawingFile = file;
                        }
                        if (e.target.files?.length) analyzeBlueprintSSE(e.target.files[0]);
                      }}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      hidden
                    />
                    <button
                      onClick={() => (window as any)._sseFileRef?.click()}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid #6366f1`, background: 'rgba(99,102,241,0.1)', color: '#818cf8', fontSize: 12, cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      <Sparkles className="w-4 h-4 inline-block mr-1" />Vision V2 (SSE) 파싱
                    </button>
                    <input
                      ref={pdfOneShotInputRef}
                      type="file"
                      accept=".pdf"
                      hidden
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        if(!file) return;
                        setEngineProgress({ active: true, step: 'upload', current: 0, total: 100, msg: 'PDF 파일 업로드 중...' });
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('formats', 'xlsx,pdf');
                          const res = await fetch((process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + "/engine/api/quote/from-pdf", {
                            method: "POST",
                            body: formData
                          });
                          if(!res.ok) {
                            try {
                              const errBody = await res.json();
                              throw new Error("엔진 원샷 파싱 실패: " + JSON.stringify(errBody.detail || errBody));
                            } catch(e) {
                              throw new Error(`통신 에러 (HTTP ${res.status}): ${e instanceof Error ? e.message : '알 수 없음'}`);
                            }
                          }
                          if (!res.body) throw new Error("Stream not supported");

                          const reader = res.body.getReader();
                          const decoder = new TextDecoder("utf-8");
                          let buffer = "";

                          while (true) {
                            const { done, value } = await reader.read();
                            if (done) break;

                            buffer += decoder.decode(value, { stream: true });
                            let newlineIdx;
                            while ((newlineIdx = buffer.indexOf("\n")) >= 0) {
                              const line = buffer.slice(0, newlineIdx).trim();
                              buffer = buffer.slice(newlineIdx + 1);

                              if (line.startsWith("data: ") && line.length > 6) {
                                try {
                                  const data = JSON.parse(line.substring(6));
                                  if (data.type === "progress") {
                                    setEngineProgress(prev => ({ ...prev, step: data.step, current: data.current || prev.current, total: data.total || prev.total, msg: data.msg || prev.msg }));
                                  } else if (data.type === "complete") {
                                    setEngineProgress(prev => ({ ...prev, msg: '원샷 처리 완료! 다운로드 중...', step: 'done', current: prev.total }));
                                    setTimeout(() => setEngineProgress(p => ({ ...p, active: false })), 1500);

                                    const { xlsx_url, pdf_url } = data.result;
                                    if (xlsx_url) {
                                      const a = document.createElement('a');
                                      a.href = (process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + xlsx_url;
                                      a.download = `견적서_원샷.xlsx`;
                                      a.click();
                                    }
                                    if (pdf_url) {
                                      const a = document.createElement('a');
                                      a.href = (process.env.NEXT_PUBLIC_NAS_GATEWAY || "https://grid.snapcodex.com").trim() + pdf_url;
                                      a.download = `견적서_원샷.pdf`;
                                      a.click();
                                    }
                                  } else if (data.type === "error") {
                                    throw new Error(data.msg);
                                  }
                                } catch(e: any) {
                                  if (e instanceof Error && !e.message.includes("Unexpected end of JSON input") && !e.message.includes("is not valid JSON")) {
                                    throw e;
                                  }
                                }
                              }
                            }
                          }
                        } catch(err: any) {
                          alert(err.message);
                          setEngineProgress(p => ({ ...p, active: false }));
                        }
                      }}
                    />
                    <button
                      onClick={() => pdfOneShotInputRef.current?.click()}
                      style={{ padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.primaryBorder}`, background: colors.primaryBg, color: '#fff', fontSize: 12, cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      <HardDrive className="w-4 h-4 inline-block mr-1" /> PDF 견적 원샷 파싱
                    </button>
                  </div>
                  {/* 우측 토글 */}
                  <button onClick={() => setShowRatios(!showRatios)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 8, border: `1px solid ${colors.borderLight}`, background: colors.surfaceAlt, color: colors.text, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <Wrench className="w-4 h-4" /> 현장 여건별 요율 상세 설정 {showRatios ? '▲' : '▼'}
                  </button>
                </div>

                {/* Local files indicator */}
                {localFiles.length > 0 && (
                  <div style={{ padding: 12, borderRadius: 8, background: colors.successBg, border: `1px solid ${colors.successBorder}`, marginTop: 12, fontSize: 12 }}>
                    <div style={{ color: colors.successText, fontWeight: 600, marginBottom: 4 }}>
                      ✓ {localFiles.length}개 파일 선택됨
                    </div>
                    <div style={{ color: colors.textSecondary, fontSize: 11 }}>
                      {localFiles.slice(0, 3).map(f => f.name).join(', ')}
                      {localFiles.length > 3 && ` 외 ${localFiles.length - 3}개`}
                    </div>
                  </div>
                )}
                
                {showRatios && (
                  <div style={{ marginTop: 12, padding: 16, borderRadius: 8, background: colors.inputBg, border: `1px solid ${colors.borderLight}` }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, color: colors.text }}>원가 구성 비율 (전체 합산 100% 권장)</div>
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(3, 1fr)' : 'repeat(7, 1fr)', gap: 12 }}>
                      {Object.entries(customRatios).map(([key, val]) => (
                        <div key={key}>
                          <label style={{ fontSize: 11, color: colors.textSecondary, display: 'block', marginBottom: 4 }}>
                            {key === 'rawMaterial' ? '원자재비' : key === 'cutting' ? '재단비' : key === 'edge' ? '엣지비' :
                             key === 'assembly' ? '조립인건비' : key === 'painting' ? '도장비' : key === 'outsourcing' ? '외주가공비' : key === 'packaging' ? '포장비' :
                             key === 'generalMgmt' ? '일반관리비' : key === 'transport' ? '운송비' : key === 'loading' ? '상하차비' : key === 'settingLabor' ? '세팅인건비' :
                             key === 'miscExpense' ? '공과잡비' : '기업이윤'}
                          </label>
                          <input 
                            type="number" step="0.01" value={val as number} 
                            onChange={e => setCustomRatios(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                            style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.inputBorder}`, background: colors.surface, color: colors.inputText, fontSize: 12 }}
                          />
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: 12, fontSize: 11, color: colors.textSecondary }}>
                      ※ 위 요율들은 총 원가 대비 비율(0.XX)로 적용되며, 현장 사정에 맞춰 조정 가능합니다.
                    </div>
                  </div>
                )}
              </div>

              {/* === 선택된 아이템 상세 보드 (도면 이미지 + 상세 속성) === */}
              {selectedItemIdx !== null && items[selectedItemIdx] && (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, marginBottom: 24, padding: isMobile ? 12 : 20, borderRadius: 12, background: colors.surfaceAlt, border: `1px solid ${colors.border}` }}>
                  {/* 좌측: 도면/이미지 목업 (업로드한 실제 이미지가 있을 때만 표시) */}
                  {(items[selectedItemIdx] as any).imageUrl && (
                    <div style={{ flex: isMobile ? 'none' : '0 0 300px', width: isMobile ? '100%' : undefined, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ height: 240, background: isDark ? '#1a1b1e' : '#e2e8f0', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${colors.border}`, position: 'relative', overflow: 'hidden' }}>
                        <ImageIcon style={{ width: 48, height: 48, color: '#94a3b8', opacity: 0.5, position: 'absolute' }} />
                        <img 
                          src={(items[selectedItemIdx] as any).imageUrl}
                          style={{ width: '100%', height: '100%', objectFit: 'cover', mixBlendMode: 'multiply', opacity: 0.9, position: 'relative', zIndex: 1, filter: 'contrast(1.1) grayscale(15%)' }}
                          alt="실제 입력 도면"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                        <div style={{ position: 'absolute', bottom: 8, right: 8, fontSize: 10, background: 'rgba(0,0,0,0.6)', color: '#fff', padding: '2px 6px', borderRadius: 4, zIndex: 2 }}>실제 도면/입면도</div>
                      </div>
                      <div style={{ fontSize: 11, color: colors.textSecondary, textAlign: 'center' }}>[제작도 뷰어] {items[selectedItemIdx].name} 상세 도면 분석</div>
                    </div>
                  )}
                  {/* 우측: 상세 속성 폼 */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.borderLight}`, paddingBottom: 8 }}>
                      <h4 style={{ margin: 0, fontSize: 14, color: colors.text, display: 'flex', alignItems: 'center', gap: 6 }}><Wrench size={16}/> 상세 견적 산출 속성 (선택된 가구)</h4>
                      <button onClick={() => setSelectedItemIdx(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted }}><XCircle size={16} /></button>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '120px 1fr', gap: isMobile ? 8 : '12px 16px', fontSize: 12, marginTop: 4 }}>
                      <div style={{ color: colors.textSecondary, paddingTop: 6, fontWeight: 500 }}>제품명 / 코드</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input value={items[selectedItemIdx].name} onChange={e => updateItem(selectedItemIdx, 'name', e.target.value)} style={{ ...getInputStyle(colors), flex: 2 }} />
                        <div style={{ display: 'flex', alignItems: 'center', background: colors.surface, border: `1px solid ${colors.inputBorder}`, borderRadius: 6, flex: 1, overflow: 'hidden' }}>
                          <span style={{ padding: '0 4px 0 8px', color: colors.textSecondary, fontSize: 12, fontWeight: 500 }}>FU-</span>
                          <input 
                            type="text" 
                            value={items[selectedItemIdx].code ? items[selectedItemIdx].code.replace(/^FU[-_]/i, '') : ''} 
                            onChange={e => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              updateItem(selectedItemIdx, 'code', val ? `FU-${val}` : '');
                            }} 
                            style={{ border: 'none', background: 'transparent', outline: 'none', color: colors.inputText, width: '100%', padding: '6px 4px', fontSize: 12 }} 
                            placeholder="01" 
                          />
                        </div>
                      </div>

                      <div style={{ color: colors.textSecondary, paddingTop: 6, fontWeight: 500 }}>규격 (W×D×H)</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input type="number" value={items[selectedItemIdx].w || ''} onChange={e => updateItem(selectedItemIdx, 'w', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), flex: 1 }} placeholder="W (Width)" />
                        <input type="number" value={items[selectedItemIdx].d || ''} onChange={e => updateItem(selectedItemIdx, 'd', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), flex: 1 }} placeholder="D (Depth)" />
                        <input type="number" value={items[selectedItemIdx].h || ''} onChange={e => updateItem(selectedItemIdx, 'h', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), flex: 1 }} placeholder="H (Height)" />
                      </div>

                      <div style={{ color: colors.textSecondary, paddingTop: 6, fontWeight: 500 }}>수량 / 기본옵션</div>
                      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <input type="number" value={items[selectedItemIdx].qty || ''} onChange={e => updateItem(selectedItemIdx, 'qty', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 80 }} />
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: colors.text }}>
                          <input type="checkbox" checked={!!(items[selectedItemIdx] as any).hasHardware} onChange={e => updateItem(selectedItemIdx, 'hasHardware', e.target.checked)} />
                          철물/하드웨어 포함 (원가 3%)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: colors.text }}>
                          <input type="checkbox" checked={!!(items[selectedItemIdx] as any).hasSocket} onChange={e => updateItem(selectedItemIdx, 'hasSocket', e.target.checked)} />
                          매립콘센트 (45,000원/개)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: colors.text }}>
                          <input type="checkbox" checked={!!(items[selectedItemIdx] as any).hasLighting} onChange={e => updateItem(selectedItemIdx, 'hasLighting', e.target.checked)} />
                          간접조명 LED (85,000원/개)
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', color: colors.text }}>
                          <input type="checkbox" checked={!!(items[selectedItemIdx] as any).hasCushion} onChange={e => updateItem(selectedItemIdx, 'hasCushion', e.target.checked)} />
                          인조가죽쿠션 (45,000원/m)
                        </label>
                      </div>

                      <div style={{ color: colors.textSecondary, paddingTop: 6, fontWeight: 500 }}>재질 및 마감</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <select style={{ ...getInputStyle(colors), flex: 1 }} onChange={e => updateItem(selectedItemIdx, 'material', e.target.value)} value={(items[selectedItemIdx] as any).material || ''}>
                          <option value="">LPM (기본)</option>
                          <option value="HPM">HPM</option>
                          <option value="PET">PET (색상다양)</option>
                          <option value="무늬목">무늬목</option>
                          <option value="우레탄도장">우레탄도장</option>
                        </select>
                        <input type="text" placeholder="색상 (예: 솔리드 화이트, 오크)" style={{ ...getInputStyle(colors), flex: 1 }} value={(items[selectedItemIdx] as any).color || ''} onChange={e => updateItem(selectedItemIdx, 'color', e.target.value)}/>
                      </div>

                      <div style={{ color: colors.textSecondary, paddingTop: 6, fontWeight: 500 }}>내부 선반/서랍</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: colors.text }}>
                        선반 <input type="number" style={{ ...getInputStyle(colors), width: 60 }} placeholder="개" value={(items[selectedItemIdx] as any).shelves || ''} onChange={e => updateItem(selectedItemIdx, 'shelves', parseInt(e.target.value)||0)} /> 개
                        <span style={{ margin: '0 8px', color: colors.borderLight }}>|</span>
                        서랍 <input type="number" style={{ ...getInputStyle(colors), width: 60 }} placeholder="개" value={(items[selectedItemIdx] as any).drawers || ''} onChange={e => updateItem(selectedItemIdx, 'drawers', parseInt(e.target.value)||0)} /> 개
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* === 좌측 작업 영역: 3. 아이템 워크벤치 === */}
              {/* 추가 + 생성 끈끈이 버튼 바 */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, position: 'sticky', top: isMobile ? 48 : 64, zIndex: 10, background: colors.bg, paddingBottom: 16, paddingTop: 8 }}>
                <button onClick={addItem} style={{ flex: 1, padding: '10px', borderRadius: 8, border: `1px dashed ${colors.border}`, background: colors.surfaceAlt, color: colors.textSecondary, cursor: 'pointer', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                  {Icons.add} 아이템 추가
                </button>
                <button onClick={() => generateEstimate()} disabled={loading || items.length === 0}
                  style={{ flex: isMobile ? undefined : 2, padding: '12px', borderRadius: 8, border: 'none', cursor: items.length === 0 ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
                    background: loading ? (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') : colors.primaryBg, color: '#fff', opacity: items.length === 0 ? 0.5 : 1 }}>
                  {loading ? '견적 생성 중...' : `견적서 생성 (${items.length}건)`}
                </button>
              </div>

              {/* 총괄 계획표 테이블 (개요 페이지 파싱 시) */}
              {masterPlan.length > 0 && (
                <div style={{ marginBottom: 24, padding: 16, borderRadius: 12, background: colors.primaryBg + '10', border: `1px solid ${colors.primaryBorder}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <LayoutList className="w-5 h-5" style={{ color: colors.text }} />
                    <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0, color: colors.text }}>가구공사 총괄 계획표 (도면 개요)</h3>
                  </div>
                  <div style={{ borderRadius: 8, overflow: 'hidden', border: `1px solid ${colors.borderLight}` }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, background: colors.surface }}>
                      <thead>
                        <tr style={{ background: colors.surfaceAlt }}>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${colors.borderLight}` }}>위치 (공간 명칭)</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${colors.borderLight}` }}>품목 코드</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${colors.borderLight}` }}>제품명</th>
                          <th style={{ padding: '8px 12px', textAlign: 'right', borderBottom: `1px solid ${colors.borderLight}` }}>계획 수량</th>
                        </tr>
                      </thead>
                      <tbody>
                        {masterPlan.map((mp, i) => (
                          <tr key={i} style={{ borderBottom: i < masterPlan.length - 1 ? `1px solid ${colors.tableBorder}` : 'none' }}>
                            <td style={{ padding: '8px 12px', color: colors.textSecondary }}>{mp.space_name || '-'}</td>
                            <td style={{ padding: '8px 12px', color: colors.textSecondary, fontWeight: 600 }}>{mp.code || '-'}</td>
                            <td style={{ padding: '8px 12px', color: colors.text }}>{mp.name || '-'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: colors.text }}>{mp.expected_qty || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* 가구 목록 테이블 */}
              <div style={{ borderRadius: 12, border: `1px solid ${colors.border}`, overflow: isMobile ? 'auto' : 'hidden', marginBottom: 16, background: colors.surface, WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: colors.tableHeaderBg }}>
                      <th style={{ width: 40, borderBottom: `1px solid ${colors.tableBorder}` }}></th>
                      {['#', '제품명', '코드', 'W', 'D', 'H', '수량', '등급', '마감재', '두께(T)', 'HW', '콘센트', '조명', '쿠션', '선반', ''].map((h, i) => (
                        <th key={i} title={h === 'HW' ? '하드웨어' : h === '콘센트' ? '매립콘센트' : h === '조명' ? '간접조명' : h === '쿠션' ? '인조가죽쿠션' : h === '선반' ? '내부 선반 개수 (비워두면 자동(8단) 계산)' : ''} style={{ padding: '10px 8px', textAlign: 'left', color: colors.tableHeaderText, fontWeight: 500, fontSize: 11, borderBottom: `1px solid ${colors.tableBorder}`, width: h === 'HW' || h === '콘센트' || h === '조명' || h === '쿠션' ? 48 : h === '선반' ? 56 : undefined }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, idx) => {
                      const isExpanded = expandedInputNos.has(idx);
                      const hasParts = (item as any).calcParts && (item as any).calcParts.length > 0;
                      return (
                        <React.Fragment key={idx}>
                          <tr 
                            onClick={() => setSelectedItemIdx(idx)}
                            style={{ borderBottom: `1px solid ${colors.tableBorder}`, cursor: 'pointer', background: selectedItemIdx === idx ? (isDark ? '#2d3748' : '#eff6ff') : 'transparent' }}
                          >
                            <td 
                              style={{ textAlign: 'center', color: colors.textSecondary }}
                              onClick={(e) => {
                                e.stopPropagation();
                                const next = new Set(expandedInputNos);
                                if (next.has(idx)) next.delete(idx);
                                else next.add(idx);
                                setExpandedInputNos(next);
                              }}
                            >
                              {hasParts ? (isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}
                            </td>
                            <td style={{ padding: '6px 8px', color: colors.tableRowText, fontSize: 12, minWidth: 46 }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span>{idx + 1}</span>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  <button onClick={(e) => { e.stopPropagation(); moveItemUp(idx); }} disabled={idx === 0} style={{ padding: 0, border: 'none', background: 'transparent', cursor: idx === 0 ? 'not-allowed' : 'pointer', opacity: idx === 0 ? 0.3 : 1, lineHeight: 0 }}><ChevronUp size={12} /></button>
                                  <button onClick={(e) => { e.stopPropagation(); moveItemDown(idx); }} disabled={idx === items.length - 1} style={{ padding: 0, border: 'none', background: 'transparent', cursor: idx === items.length - 1 ? 'not-allowed' : 'pointer', opacity: idx === items.length - 1 ? 0.3 : 1, lineHeight: 0 }}><ChevronDown size={12} /></button>
                                </div>
                              </div>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}><input value={item.name} onChange={e => updateItem(idx, 'name', e.target.value)} style={getInputStyle(colors)} placeholder="제품명" /></td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <div style={{ display: 'flex', alignItems: 'center', background: colors.surface, border: `1px solid ${colors.inputBorder}`, borderRadius: 6, width: 70, overflow: 'hidden' }}>
                                <span style={{ padding: '0 2px 0 6px', color: colors.textSecondary, fontSize: 12, fontWeight: 500 }}>FU-</span>
                                <input 
                                  type="text" 
                                  value={item.code ? item.code.replace(/^FU[-_]/i, '') : ''} 
                                  onChange={e => {
                                    const val = e.target.value.replace(/[^0-9]/g, '');
                                    updateItem(idx, 'code', val ? `FU-${val}` : '');
                                  }} 
                                  style={{ border: 'none', background: 'transparent', outline: 'none', color: colors.inputText, width: '100%', padding: '6px 2px', fontSize: 12 }} 
                                  placeholder="01" 
                                />
                              </div>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}><input type="number" value={item.w || ''} onChange={e => updateItem(idx, 'w', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 65 }} /></td>
                            <td onClick={(e) => e.stopPropagation()}><input type="number" value={item.d || ''} onChange={e => updateItem(idx, 'd', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 65 }} /></td>
                            <td onClick={(e) => e.stopPropagation()}><input type="number" value={item.h || ''} onChange={e => updateItem(idx, 'h', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 65 }} /></td>
                            <td onClick={(e) => e.stopPropagation()}><input type="number" value={item.qty || ''} onChange={e => updateItem(idx, 'qty', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 50 }} /></td>
                            {/* 가격 등급 드롭다운 */}
                            <td onClick={(e) => e.stopPropagation()}>
                              <select
                                value={(item as any).priceGrade || priceGrade}
                                onChange={e => updateItem(idx, 'priceGrade', e.target.value)}
                                style={{ ...getInputStyle(colors), width: 75, fontSize: 11 }}
                              >
                                <option value="min">최저가</option>
                                <option value="avg">평균가</option>
                                <option value="high">고급형</option>
                                <option value="max">최고급형</option>
                              </select>
                            </td>
                            {/* 마감재 드롭다운 + AI 두께 추천 */}
                            <td onClick={(e) => e.stopPropagation()}>
                              <select
                                value={(item as any).finishMaterial || 'LPM'}
                                onChange={e => {
                                  const fm = e.target.value;
                                  const thickMap: Record<string, number> = {
                                    'LPM': 18, 'MDF(도장)': 18, 'MDF(필름)': 18, 'PB': 15,
                                    '자작합판': 18, '오크집성판': 18, '라왕집성판': 18,
                                  };
                                  updateItem(idx, 'finishMaterial', fm);
                                  updateItem(idx, 'thickness', thickMap[fm] ?? 18);
                                }}
                                style={{ ...getInputStyle(colors), width: 90, fontSize: 11 }}
                              >
                                {['PB', 'LPM', 'MDF(도장)', 'MDF(필름)', '자작합판', '오크집성판', '라왕집성판'].map(m => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </td>
                            <td onClick={(e) => e.stopPropagation()}>
                              <input type="number" value={(item as any).thickness || ''} onChange={e => updateItem(idx, 'thickness', parseInt(e.target.value) || 0)} style={{ ...getInputStyle(colors), width: 50 }} />
                            </td>
                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}><input type="checkbox" checked={!!(item as any).hasHardware} onChange={e => updateItem(idx, 'hasHardware', e.target.checked)} /></td>
                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}><input type="checkbox" checked={!!(item as any).hasSocket} onChange={e => updateItem(idx, 'hasSocket', e.target.checked)} /></td>
                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}><input type="checkbox" checked={!!(item as any).hasLighting} onChange={e => updateItem(idx, 'hasLighting', e.target.checked)} /></td>
                            <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}><input type="checkbox" checked={!!(item as any).hasCushion} onChange={e => updateItem(idx, 'hasCushion', e.target.checked)} /></td>
                            <td onClick={(e) => e.stopPropagation()}><input type="number" placeholder="자동" value={(item as any).shelfCount ?? ''} onChange={e => { const v = e.target.value; updateItem(idx, 'shelfCount', v === '' ? null : parseInt(v)); }} style={{ ...getInputStyle(colors), width: 45, fontSize: 11 }} /></td>
                            <td onClick={(e) => e.stopPropagation()}><button onClick={() => removeItem(idx)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>×</button></td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={15} style={{ padding: '0 12px 16px 48px', background: isDark ? 'rgba(255,115,22,0.03)' : 'rgba(234,88,12,0.02)' }}>
                                <div style={{ borderLeft: `2px solid ${colors.omniBorder}`, paddingLeft: 16, marginTop: 4 }}>
                                  {(() => {
                                    // Live calculation for preview
                                    const mType = estimateMaterial(item.name, item.category || '');
                                    const pModel = PRICE_MODELS.find(m => m.category === item.category) || PRICE_MODELS[0];
                                    const grade = ((item as any).priceGrade || priceGrade) as 'min'|'avg'|'high'|'max';
                                    const basePrice = pModel.pricePerM2[grade];
                                    const cb = calcCostBreakdown(basePrice, mType, estimateType, customRatios, undefined, {
                                      w: item.w, d: item.d, h: item.h,
                                      thickness: (item as any).thickness || 18,
                                      hasHardware: (item as any).hasHardware,
                                      hasSocket: (item as any).hasSocket,
                                      hasLighting: (item as any).hasLighting,
                                      hasCushion: (item as any).hasCushion,
                                      shelfCount: (item as any).shelfCount,
                                      category: item.category
                                    });
                                    const surfaceM2 = 2 * ((item.w/1000)*(item.d/1000) + (item.w/1000)*(item.h/1000) + (item.d/1000)*(item.h/1000));
                                    const unitPrice = cb.totalOriginCost;
                                    const totalPrice = unitPrice * (item.qty || 1);
                                    const calcParts = (item as any).calcParts || cb.autoParts || [];

                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                        {/* 1. 가구품번별 개요 요약 */}
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, padding: 12, background: colors.surface, borderRadius: 8, border: `1px solid ${colors.borderLight}` }}>
                                          <div><span style={{color:colors.textSecondary, fontSize:11}}>규격:</span> <span style={{fontWeight:600, color:colors.text}}>{item.w} x {item.d} x {item.h} mm</span></div>
                                          <div><span style={{color:colors.textSecondary, fontSize:11}}>표면적:</span> <span style={{fontWeight:600, color:colors.text}}>{surfaceM2.toFixed(2)} ㎡</span></div>
                                          <div><span style={{color:colors.textSecondary, fontSize:11}}>마감재:</span> <span style={{fontWeight:600, color:colors.text}}>{cb.materialLabel} ({(item as any).thickness||18}T)</span></div>
                                          <div><span style={{color:colors.textSecondary, fontSize:11}}>기본단가:</span> <span style={{fontWeight:600, color:colors.text}}>{fmt(basePrice)} 원/㎡</span></div>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'flex-start' }}>
                                          <div style={{ flexShrink: 0, width: isMobile ? '100%' : 280, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {/* 2. 모듈분할계획 (3D View + Module logic) */}
                                            <div style={{ background: colors.surface, padding: 12, borderRadius: 8, border: `1px solid ${colors.borderLight}` }}>
                                              <div style={{ fontSize: 11, fontWeight: 700, color: colors.primaryText, marginBottom: 8 }}>[모듈분할계획 & 3D VIEW]</div>
                                              <FurnitureViewer3D
                                                item={{
                                                  name: item.name,
                                                  w: item.w / 1000,
                                                  d: item.d / 1000,
                                                  h: item.h / 1000,
                                                  openRatio: (item as any).openRatio || 0,
                                                  finishMaterial: (item as any).finishMaterial || 'LPM',
                                                  thickness: (item as any).thickness || 18,
                                                  calcParts: calcParts,
                                                  hoveredPart: hoveredParts[idx],
                                                }}
                                                width="100%"
                                                height={220}
                                                showCapture={true}
                                              />
                                              {/* 파츠 라벨 */}
                                              {calcParts.length > 0 && (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 12 }}>
                                                  {calcParts.map((p: any, pi: number) => {
                                                    const isHovered = hoveredParts[idx] === p.partName;
                                                    return (
                                                      <div 
                                                        key={pi} 
                                                        onMouseEnter={() => setHoveredParts(prev => ({...prev, [idx]: p.partName}))}
                                                        onMouseLeave={() => setHoveredParts(prev => ({...prev, [idx]: ''}))}
                                                        style={{ 
                                                          padding: '2px 6px', borderRadius: 4, 
                                                          background: isHovered ? 'rgba(249, 115, 22, 0.1)' : colors.primaryBgLight, 
                                                          border: `1px solid ${isHovered ? '#f97316' : 'transparent'}`, 
                                                          fontSize: 10, cursor: 'pointer', transition: 'all 0.2s',
                                                          color: isHovered ? '#f97316' : colors.primaryText
                                                        }}
                                                      >
                                                        {p.partName}
                                                      </div>
                                                    )
                                                  })}
                                                </div>
                                              )}
                                            </div>
                                          </div>

                                          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16 }}>
                                            {/* 3. 자재산출근거 */}
                                            <div style={{ background: colors.surface, padding: 12, borderRadius: 8, border: `1px solid ${colors.borderLight}` }}>
                                              <div style={{ fontSize: 11, fontWeight: 700, color: colors.primaryText, marginBottom: 8 }}>[자재산출근거 상세내역]</div>
                                              <div style={{ overflowX: 'auto' }}>
                                                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
                                                  <thead>
                                                    <tr style={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.borderLight}` }}>
                                                      <th style={{ textAlign: 'left', padding: '4px 0' }}>파츠명</th>
                                                      <th style={{ textAlign: 'left', padding: '4px 0' }}>재질/규격</th>
                                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>W</th>
                                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>D</th>
                                                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>H</th>
                                                      <th style={{ textAlign: 'left', padding: '4px 0' }}>산출식/소요량</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {calcParts.map((p: any, pi: number) => (
                                                      <tr key={pi} style={{ borderBottom: pi === calcParts.length - 1 ? 'none' : `1px solid ${colors.borderLight}` }}>
                                                        <td style={{ padding: '6px 0', color: colors.text, fontWeight: 500 }}>{p.partName}</td>
                                                        <td style={{ padding: '6px 0', color: colors.textSecondary }}>{p.material}</td>
                                                        <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.w || '-'}</td>
                                                        <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.d || '-'}</td>
                                                        <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.h || '-'}</td>
                                                        <td style={{ padding: '6px 0', color: colors.successText }}>{p.calculation}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                              <div style={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 16, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${colors.borderLight}`, fontSize: 11 }}>
                                                <div style={{color:colors.textSecondary}}>원자재: <span style={{fontWeight:600, color:colors.text}}>{fmt(cb.rawMaterialCost)}원</span></div>
                                                <div style={{color:colors.textSecondary}}>재단비: <span style={{fontWeight:600, color:colors.text}}>{fmt(cb.cuttingCost)}원</span></div>
                                                <div style={{color:colors.textSecondary}}>엣지비: <span style={{fontWeight:600, color:colors.text}}>{fmt(cb.edgeCost)}원</span></div>
                                                <div style={{color:colors.primaryText}}>자재비 소계: <span style={{fontWeight:600}}>{fmt(cb.materialTotal)}원</span></div>
                                              </div>
                                            </div>

                                            {/* 4. 액세서리 및 제조가공비 산출근거 */}
                                            <div style={{ background: colors.surface, padding: 12, borderRadius: 8, border: `1px solid ${colors.borderLight}` }}>
                                              <div style={{ fontSize: 11, fontWeight: 700, color: colors.primaryText, marginBottom: 8 }}>[제조/액세서리 산출근거]</div>
                                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color:colors.textSecondary }}><span>조립인건비 ({cb.assemblyLaborDays}인)</span> <span style={{color:colors.text}}>{fmt(cb.assemblyCost)}원</span></div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color:colors.textSecondary }}><span>도장/마감 ({cb.paintingLaborDays}인)</span> <span style={{color:colors.text}}>{fmt(cb.paintingCost)}원</span></div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color:colors.textSecondary }}><span>하드웨어/옵션</span> <span style={{color:colors.text}}>{fmt(cb.optionsCost)}원</span></div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color:colors.textSecondary }}><span>포장/보양</span> <span style={{color:colors.text}}>{fmt(cb.packagingCost)}원</span></div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', color:colors.primaryText, gridColumn: '1 / -1', marginTop: 4, paddingTop: 4, borderTop: `1px solid ${colors.borderLight}` }}>
                                                  <span style={{fontWeight:600}}>제조/옵션 소계</span> <span style={{fontWeight:600}}>{fmt(cb.manufacturingTotal)}원</span>
                                                </div>
                                              </div>
                                            </div>

                                            {/* 5, 6. 아이템별 소계 및 집계 */}
                                            <div style={{ background: colors.surface, padding: 12, borderRadius: 8, border: `1px solid ${colors.borderLight}` }}>
                                              <div style={{ fontSize: 11, fontWeight: 700, color: colors.primaryText, marginBottom: 8 }}>[품번별 집계 (1EA 기준)]</div>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', fontSize: 11, fontWeight: 500, color:colors.textSecondary, borderBottom: `1px solid ${colors.borderLight}`, paddingBottom: 6, marginBottom: 6 }}>
                                                <span>자재: {fmt(cb.materialTotal)}</span>
                                                <span>제조: {fmt(cb.manufacturingTotal)}</span>
                                                <span>물류/설치: {fmt(cb.installTotal)}</span>
                                                <span>일반/이윤: {fmt(cb.generalMgmtCost + cb.miscExpenseCost + cb.profitCost)}</span>
                                              </div>
                                              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', fontSize: 13, fontWeight: 700, color: colors.successText, marginTop: 8 }}>
                                                <span>1EA 단가: {fmt(unitPrice)} 원</span>
                                                <span>수량: {item.qty || 1} EA</span>
                                                <span>총 금액: {fmt(totalPrice)} 원</span>
                                              </div>
                                            </div>

                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: colors.tableHeaderBg, borderTop: `2px solid ${colors.primaryBorder}` }}>
                      <td colSpan={10} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 700, color: colors.text }}>전체 가구공사 총 실시간 예상 집계 (공급가액 기준):</td>
                      <td colSpan={5} style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 800, color: colors.successText, fontSize: 16 }}>
                        {(() => {
                          const tempEstimate = generateEstimateLogic({
                            projectName: 'temp',
                            items: items.map(i => ({
                              name: i.name || '',
                              code: i.code || '',
                              location: (i as any).location || '',
                              w: Number(i.w) || 0,
                              d: Number(i.d) || 0,
                              h: Number(i.h) || 0,
                              qty: Number(i.qty) || 1,
                              hasHardware: !!(i as any).hasHardware,
                              hasSocket: !!(i as any).hasSocket,
                              hasLighting: !!(i as any).hasLighting,
                              hasCushion: !!(i as any).hasCushion,
                              shelfCount: (i as any).shelfCount,
                              thickness: (i as any).thickness || 18,
                              category: i.category,
                              finishMaterial: (i as any).finishMaterial || 'LPM',
                              openRatio: Number((i as any).openRatio) || 0,
                              calcParts: (i as any).calcParts,
                              moduleBreakdown: (i as any).moduleBreakdown
                            })),
                            priceGrade: priceGrade as 'min'|'avg'|'max',
                            estimateType: estimateType as 'designinsight' | 'narajangteo' | 'compare_3',
                            customRatios: customRatios,
                          });
                          const liveTotal = tempEstimate.subtotal;
                          return fmt(liveTotal) + ' 원';
                        })()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* 추가/실행 버튼 영역 (상단으로 끈끈이 이동됨) */}
            </div>
          )}

          {/* ── TAB: NAS 도면 ── */}
          {tab === 'nas' && (
            <div>
              <div style={{ padding: 24, borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  {Icons.nas}
                  <span style={{ fontSize: 14, fontWeight: 600, color: colors.text }}>NAS 도면 경로</span>
                </div>
                <input value={nasPath} onChange={e => setNasPath(e.target.value)} placeholder="B_DESIGN/[2025_PROJECT]/ID_25XX_프로젝트A/03_DWG/가구도면.dxf"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 14, marginBottom: 12 }} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                  <input value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="프로젝트명"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.inputBorder}`, background: colors.inputBg, color: colors.inputText, fontSize: 13 }} />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select value={sourceType} onChange={e => setSourceType(e.target.value as any)}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}>
                      <option value="dxf">CAD 도면 (DXF/PDF)</option>
                      <option value="skp">SketchUp (SKP)</option>
                      <option value="photo">가구 사진 (IMG)</option>
                    </select>
                    <select value={estimateType} onChange={e => setEstimateType(e.target.value as any)}
                      style={{ flex: 1, padding: '10px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: 13 }}>
                      <option value="designinsight">디자인인사이트 기준</option>
                      <option value="narajangteo">나라장터 등록가</option>
                      <option value="compare_3">3사 비교단가</option>
                    </select>
                  </div>
                </div>
                <button onClick={generateFromNas} disabled={loading || !nasPath.trim()}
                  style={{ width: '100%', padding: '14px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    background: 'linear-gradient(135deg, #f97316, #ef4444)', color: '#fff', opacity: !nasPath.trim() ? 0.5 : 1 }}>
 {loading ? '도면 분석 중...' : '도면 분석 → 견적서 생성'}
                </button>
              </div>
              <div style={{ padding: 16, borderRadius: 10, background: 'rgba(249,115,22,0.05)', border: '1px solid rgba(249,115,22,0.15)', fontSize: 12, color: '#94a3b8', lineHeight: 1.8 }}>
 <strong style={{ color: '#f97316' }}><Lightbulb className="w-4 h-4 inline-block" />지원 형식:</strong><br />
                • <strong>DXF</strong> — FU-XX 블록 자동 추출 (INSERT + ATTRIB)<br />
                • <strong>PDF</strong> — Gemini AI 기반 가구 스케줄 인식<br />
                • <strong>텍스트</strong> — 가구 목록 파싱<br /><br />
 <strong style={{ color: '#f97316' }}><Folder className="w-4 h-4 inline-block" />경로 예시:</strong><br />
                B_DESIGN/[2025_PROJECT]/ID_25XX_프로젝트A/03_DWG/가구도면.dxf
              </div>
            </div>
          )}

          {/* ── TAB: 단가 모델 ── */}
          {tab === 'models' && (
            <div>
              <div style={{ marginBottom: 16, fontSize: 13, color: colors.textSecondary }}>
                A 프로젝트 (93건, 공급가 2.249억원) 기반 카테고리별 단가 모델
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
                {models.map(m => (
                  <div key={m.category} style={{ padding: 16, borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.surfaceAlt }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: colors.primaryText, marginBottom: 4 }}>{m.label}</div>
                    <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 8 }}>{m.description}</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 4, fontSize: 11 }}>
	                      <div style={{ padding: '6px 8px', borderRadius: 6, background: colors.successBg, textAlign: 'center' }}>
                        <div style={{ color: colors.successText }}>최저</div>
                        <div style={{ color: colors.text, fontWeight: 600, fontSize: 12 }}>{fmt(m.pricePerM2.min)}</div>
	                      </div>
	                      <div style={{ padding: '6px 8px', borderRadius: 6, background: colors.primaryBgLight, textAlign: 'center' }}>
                        <div style={{ color: colors.primaryText }}>평균</div>
                        <div style={{ color: colors.text, fontWeight: 600, fontSize: 12 }}>{fmt(m.pricePerM2.avg)}</div>
	                      </div>
	                      <div style={{ padding: '6px 8px', borderRadius: 6, background: colors.errorBg, textAlign: 'center' }}>
	                        <div style={{ color: colors.errorText }}>최고</div>
                        <div style={{ color: colors.text, fontWeight: 600, fontSize: 12 }}>{fmt(m.pricePerM2.max)}</div>
	                      </div>
	                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: colors.textMuted }}>참조: {m.count}건 · 원/㎡</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB: 견적서 문서함 (Archive) ── */}
          {tab === 'archive' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', color: colors.text }}><Archive className="w-5 h-5 inline-block mr-2" />견적서 문서함</h2>
                  <div style={{ fontSize: 12, color: colors.textSecondary }}>과거에 작성된 견적서를 열람하거나 다운로드합니다.</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
                {savedEstimates.length === 0 ? (
                  <div style={{ color: colors.textSecondary, fontSize: 13, padding: 20 }}>저장된 문서가 없습니다. 견적서 산출 후 '문서함 저장' 버튼을 클릭하세요.</div>
                ) : savedEstimates.map((doc, idx) => (
                  <div key={doc.id || idx} style={{ padding: 20, borderRadius: 12, background: colors.surfaceAlt, border: `1px solid ${colors.border}`, display: 'flex', flexDirection: 'column', gap: 12, transition: 'all 0.2s', position: 'relative' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ padding: '4px 8px', borderRadius: 4, background: colors.primaryBgLight, color: colors.primaryText, fontSize: 10, fontWeight: 600 }}>{doc.title || doc.type}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ fontSize: 11, color: colors.textSecondary }}>{doc.createdAt ? new Date(doc.createdAt).toLocaleDateString('ko-KR') : doc.date}</div>
                        <button onClick={(e) => { e.stopPropagation(); deleteFromArchive(doc.id); }} style={{ background: 'none', border: 'none', color: colors.errorText, cursor: 'pointer', padding: 0 }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: colors.text, cursor: 'pointer' }} onClick={() => loadFromArchive(doc)}>{doc.projectName || doc.name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, paddingBottom: 12, borderBottom: `1px solid ${colors.border}` }}>
                      <span style={{ color: colors.textSecondary }}>포함 항목</span>
                      <span style={{ color: colors.text }}>{doc.itemCount ?? doc.itemsCount ?? 0}종</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: colors.textSecondary }}>총 견적가액</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: colors.successText }}>{fmt(doc.totalPrice ?? doc.total ?? 0)}원</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button style={{ flex: 1, padding: '8px', borderRadius: 6, background: colors.bg, border: `1px solid ${colors.border}`, color: colors.textSecondary, fontSize: 12, cursor: 'pointer' }} onClick={() => loadFromArchive(doc)}>작업실 복귀(수정)</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── TAB: 자재&마감재 가이드 (Materials) ── */}
          {tab === 'materials' && (
            <div style={{ animation: 'fadeIn 0.3s ease-out' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <div>
                  <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px', color: colors.text }}><Package className="w-5 h-5 inline-block mr-2" />제작가구 자재 및 마감재 가이드</h2>
                  <div style={{ fontSize: 13, color: colors.textSecondary }}>견적 산출 및 도면 해독 시 메인 AI가 기준으로 삼는 제작가구 자재 데이터입니다.</div>
                </div>
              </div>

              {/* 등급 안내 */}
              <div style={{ marginBottom: 24, padding: 20, borderRadius: 12, background: colors.surfaceAlt, border: `1px solid ${colors.borderLight}` }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 16 }}>🌿 친환경 자재 등급 안내 (포름알데히드 방출량 기준)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                  <div style={{ background: colors.bg, padding: 16, borderRadius: 8, border: `1px solid ${colors.successText}50` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, color: colors.successText, fontSize: 16 }}>SE0</span>
                      <span style={{ fontSize: 11, color: colors.textMuted }}>0.3mg/L 이하</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
                      최고급 친환경 내장재. 인체에 가장 무해하며 병원, 유치원 등에 적극 사용.
                    </div>
                  </div>
                  <div style={{ background: colors.bg, padding: 16, borderRadius: 8, border: `1px solid ${colors.primaryBorder}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, color: colors.primaryText, fontSize: 16 }}>E0</span>
                      <span style={{ fontSize: 11, color: colors.textMuted }}>0.5mg/L 이하</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
                      조달청 및 1군 브랜드 기본 스펙. 실내에 전반적으로 쓰이는 우수 친환경 등급.
                    </div>
                  </div>
                  <div style={{ background: colors.bg, padding: 16, borderRadius: 8, border: `1px solid ${colors.borderMedium}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, color: colors.text, fontSize: 16 }}>E1</span>
                      <span style={{ fontSize: 11, color: colors.textMuted }}>1.5mg/L 이하</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }}>
                      가정용으로 허용된 최하 등급. 공공기관이나 민감한 실내 사용엔 기피되는 추세.
                    </div>
                  </div>
                  <div style={{ background: colors.bg, padding: 16, borderRadius: 8, border: `1px solid ${colors.errorText}30` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontWeight: 800, color: colors.errorText, fontSize: 16 }}>E2</span>
                      <span style={{ fontSize: 11, color: colors.textMuted }}>5.0mg/L 이하</span>
                    </div>
                    <div style={{ fontSize: 12, color: colors.errorText, lineHeight: 1.5, opacity: 0.8 }}>
                      실내 가구용으로 사용 금지 (비규격 자재).
                    </div>
                  </div>
                </div>
              </div>

              {/* 자재 스펙 비교표 */}
              <h3 style={{ fontSize: 15, fontWeight: 700, color: colors.text, marginBottom: 12 }}>🔹 주요 보드 및 표면 마감(Finish) 단가 비교표</h3>
              <div style={{ background: colors.surfaceAlt, borderRadius: 12, overflow: isMobile ? 'auto' : 'hidden', border: `1px solid ${colors.borderLight}`, WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: colors.bg, borderBottom: `2px solid ${colors.borderMedium}` }}>
                      <th style={{ padding: '14px 16px', color: colors.text, fontWeight: 600, width: '15%' }}>분류</th>
                      <th style={{ padding: '14px 16px', color: colors.text, fontWeight: 600, width: '15%' }}>심재(Core)</th>
                      <th style={{ padding: '14px 16px', color: colors.text, fontWeight: 600, width: '45%' }}>디자인 관점 특장점 및 주로 쓰이는 곳</th>
                      <th style={{ padding: '14px 16px', color: colors.text, fontWeight: 600, width: '25%', textAlign: 'right' }}>단가 수준 (18T)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '16px', fontWeight: 600, color: colors.text }}>LPM</td>
                      <td style={{ padding: '16px', color: colors.textSecondary }}>PB (파티클보드)</td>
                      <td style={{ padding: '16px', color: colors.textSecondary, lineHeight: 1.5 }}>저압 멜라민 함침지를 열압착. 단가가 가장 저렴하여 책장/수납장 등 <strong>바디(몸통)</strong>나 보급형 도어에 흔히 쓰임.</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: colors.successText + '15', color: colors.successText }}>저가 (기준점)</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '16px', fontWeight: 600, color: colors.text }}>PET (솔리드/매트)</td>
                      <td style={{ padding: '16px', color: colors.textSecondary }}>MDF</td>
                      <td style={{ padding: '16px', color: colors.textSecondary, lineHeight: 1.5 }}>변색이 적고 매끄러운 텍스처(매트/글로시) 표현 가능. 최근 고가 시스템장이나 부엌가구 <strong>도어(문짝)</strong>로 각광.</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: colors.primaryText + '15', color: colors.primaryText }}>중가 (LPM의 2~3배)</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '16px', fontWeight: 600, color: colors.text }}>HPM (호마이카)</td>
                      <td style={{ padding: '16px', color: colors.textSecondary }}>PB / MDF</td>
                      <td style={{ padding: '16px', color: colors.textSecondary, lineHeight: 1.5 }}>표면강도와 스크래치 내성이 극강. 식당 테이블이나 학교 데스크 등 <strong>데스크 상판(TOP)</strong>에 필수적으로 적용됨.</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: colors.warningText + '15', color: colors.warningText }}>중고가</span>
                      </td>
                    </tr>
                    <tr style={{ borderBottom: `1px solid ${colors.borderLight}` }}>
                      <td style={{ padding: '16px', fontWeight: 600, color: colors.text }}>무늬목 (Veneer)</td>
                      <td style={{ padding: '16px', color: colors.textSecondary }}>MDF</td>
                      <td style={{ padding: '16px', color: colors.textSecondary, lineHeight: 1.5 }}>천연 원목을 얇게 켜서 부착하므로 가장 리얼한 나무 질감. 변색과 스크래치에 취약하나 <strong>고급 회의실 가구</strong>에 사용.</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: colors.errorText + '15', color: colors.errorText }}>고가</span>
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '16px', fontWeight: 600, color: colors.text }}>우레탄 도장</td>
                      <td style={{ padding: '16px', color: colors.textSecondary }}>MDF</td>
                      <td style={{ padding: '16px', color: colors.textSecondary, lineHeight: 1.5 }}>액체 도료를 여러 번 분사해 건조. 엣지 이음새가 전무하여 <strong>R가공(곡면) 아트월 가구</strong> 등에 불가피하게 사용됨.</td>
                      <td style={{ padding: '16px', textAlign: 'right' }}>
                        <span style={{ fontSize: 12, fontWeight: 600, padding: '4px 8px', borderRadius: 4, background: '#9333ea15', color: '#9333ea' }}>최고가</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 16, padding: '12px 16px', background: colors.primaryBgLight, border: `1px solid ${colors.primaryBorder}`, borderRadius: 8, fontSize: 12, color: colors.primaryText, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Lightbulb size={16} className="flex-shrink-0" />
                <span>에이전트 기본 산식: 별도의 마감 지시가 없는 일반 사무용 가구는 <strong>몸통=LPM(PB), 문짝=PET(MDF) 18T</strong>를 디폴트로 적용하며, 엣지는 1.0~1.2mm ABS 엣지가 부착된다고 가정하여 컷팅/재단 비용을 역산합니다.</span>
              </div>
            </div>
          )}



          {/* ── 견적 결과 테이블 ── */}
          {estimate && (
            <div style={{ marginTop: isMobile ? 16 : 24 }}>
              {/* === 견적 결과 상단 Sticky Bar === */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', marginBottom: 12, gap: isMobile ? 8 : 0, position: 'sticky', top: isMobile ? 110 : 130, zIndex: 9, background: colors.bg, padding: '8px 0' }}>
                <h3 style={{ fontSize: isMobile ? 14 : 16, fontWeight: 600, color: colors.text, margin: 0 }}><ClipboardList className="w-4 h-4 inline-block mr-1" />{estimate.projectName} — 견적 결과</h3>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button onClick={saveToArchive} title="현재 설정/수치 등 문서함에 저장" style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.omniBorder || '#f97316'}`, background: 'rgba(249, 115, 22, 0.1)', color: '#ea580c', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 'bold' }}>
                    <HardDrive className="w-4 h-4 inline-block" /> 버전 기록하여 문서함 저장
                  </button>
                  <button onClick={() => setShowPdfModal(true)} title="52페이지 상세 구조 (부록 포함)" style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.primaryBorder || '#e2e8f0'}`, background: colors.primaryBg || '#fff', color: '#fff', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    <FileText className="w-4 h-4 inline-block" />📄 표준품셈 PDF
                  </button>
                  <button onClick={downloadCSV} title="단순 쉼표 구분 텍스트" style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.successBorder}`, background: colors.successBg, color: colors.successText, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {Icons.download} CSV
                  </button>
                  <button onClick={() => { handleGenerateDocs(["xlsx"]); }} title="AntiGravity 엔진: 조달청 규격 부재산출서 XLSX 생성" style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.primaryBorder}`, background: colors.primaryBgLight, color: colors.primaryText, fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {Icons.download} 📊 (Engine) 납품용 엑셀 산출서
                  </button>
                </div>
              </div>
              <div style={{ borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'auto', maxHeight: isMobile ? 300 : 400, background: colors.surface, WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: colors.tableHeaderBg, position: 'sticky', top: 0 }}>
                      <th style={{ width: 40 }}></th>
                      {['NO', '제품명', '코드', 'W', 'D', 'H', '수량', '단가', '금액', '카테고리', ''].map((h, i) => (
                        <th key={i} style={{ padding: '10px 8px', textAlign: i >= 3 && h ? 'right' : 'left', color: colors.tableHeaderText, fontWeight: 500, fontSize: 11, borderBottom: `1px solid ${colors.tableBorder}` }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {estimate.items.map((item) => {
                      const isExpanded = expandedItemNos.has(item.no);
                      return (
                        <React.Fragment key={item.no}>
                          <tr 
                            style={{ borderBottom: `1px solid ${colors.tableBorder}`, cursor: 'pointer' }}
                            onClick={() => {
                              const next = new Set(expandedItemNos);
                              if (next.has(item.no)) next.delete(item.no);
                              else next.add(item.no);
                              setExpandedItemNos(next);
                            }}
                          >
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              {isExpanded ? <ChevronDown size={14} color={colors.textSecondary} /> : <ChevronRight size={14} color={colors.textSecondary} />}
                            </td>
                            <td style={{ padding: '8px', color: colors.tableRowText }}>{item.no}</td>
                            <td style={{ padding: '8px', color: colors.tableRowMain, fontWeight: 500 }}>{item.name}</td>
                            <td style={{ padding: '8px', color: colors.tableRowText }}>{item.code}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.tableRowText }}>{item.w}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.tableRowText }}>{item.d}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.tableRowText }}>{item.h}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.text }}>
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9', padding: '2px 6px', borderRadius: 6 }}>
                                <button onClick={(e) => { e.stopPropagation(); updateEstimateItem(item.no, 'dec'); }} style={{ color: colors.textSecondary, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: 14 }}>-</button>
                                <span style={{ width: 16, textAlign: 'center', fontSize: 12, fontWeight: 600 }}>{item.qty}</span>
                                <button onClick={(e) => { e.stopPropagation(); updateEstimateItem(item.no, 'inc'); }} style={{ color: colors.textSecondary, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px', fontSize: 14 }}>+</button>
                              </div>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.primaryText }}>{fmt(item.unitPrice)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', color: colors.successText, fontWeight: 600 }}>{fmt(item.totalPrice)}</td>
                            <td style={{ padding: '8px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 4, background: colors.primaryBgLight, color: colors.primaryText, fontSize: 10 }}>
                                {item.category.replace(/_/g, ' ')}
                              </span>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <button onClick={(e) => { e.stopPropagation(); updateEstimateItem(item.no, 'delete'); }} 
                                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', opacity: 0.8, padding: 4 }} title="항목 삭제">
                                <Trash2 size={14} color={colors.errorText} />
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={12} style={{ padding: '0 8px 16px 48px', background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                                <div style={{ borderLeft: `2px solid ${colors.primaryBorder}`, paddingLeft: 16, marginTop: 4 }}>
                                  {/* 3D 뷰 + 파츠 테이블 나란히 */}
                                  <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, alignItems: 'flex-start' }}>
                                    {/* 3D 파라메트릭 뷰 */}
                                    <div style={{ flexShrink: 0, width: isMobile ? '100%' : 280 }}>
                                      <div style={{ fontSize: 10, fontWeight: 700, color: colors.primaryText, marginBottom: 6, letterSpacing: '0.4px' }}>
                                        🎲 3D ISO VIEW
                                      </div>
                                      <FurnitureViewer3D
                                        item={{
                                          name: item.name,
                                          w: item.w / 1000,
                                          d: item.d / 1000,
                                          h: item.h / 1000,
                                          openRatio: (item as any).openRatio || 0,
                                          finishMaterial: (item as any).finishMaterial || item.category?.toLowerCase().includes('desk') ? 'LPM' : 'LPM',
                                          thickness: (item as any).thickness || 18,
                                          calcParts: item.calcParts || [],
                                        }}
                                        width={isMobile ? "100%" : 280}
                                        height={isMobile ? 180 : 220}
                                        showCapture={true}
                                      />
                                    </div>
                                    {/* 파츠 상세 테이블 */}
                                    {item.calcParts && item.calcParts.length > 0 && (
                                      <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 11, fontWeight: 600, color: colors.primaryText, marginBottom: 8 }}>[상세 자재 소요량 산출근거]</div>
                                        <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                                          <thead>
                                            <tr style={{ color: colors.textMuted, borderBottom: `1px solid ${colors.borderLight}` }}>
                                              <th style={{ textAlign: 'left', padding: '4px 0' }}>파츠명</th>
                                              <th style={{ textAlign: 'left', padding: '4px 0' }}>재질/규격</th>
                                              <th style={{ textAlign: 'right', padding: '4px 8px' }}>W</th>
                                              <th style={{ textAlign: 'right', padding: '4px 8px' }}>D</th>
                                              <th style={{ textAlign: 'right', padding: '4px 8px' }}>H</th>
                                              <th style={{ textAlign: 'left', padding: '4px 0' }}>산출식/소요량</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {item.calcParts.map((p: any, pi: number) => (
                                              <tr key={pi} style={{ borderBottom: pi === item.calcParts!.length - 1 ? 'none' : `1px solid ${colors.borderLight}` }}>
                                                <td style={{ padding: '6px 0', color: colors.text, fontWeight: 500 }}>{p.partName}</td>
                                                <td style={{ padding: '6px 0', color: colors.textSecondary }}>{p.material}</td>
                                                <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.w || '-'}</td>
                                                <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.d || '-'}</td>
                                                <td style={{ padding: '6px 8px', textAlign: 'right', color: colors.textSecondary }}>{p.h || '-'}</td>
                                                <td style={{ padding: '6px 0', color: colors.successText }}>{p.calculation}</td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {estimate.warnings.length > 0 && (
                <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: colors.warningBg, border: `1px solid ${colors.warningBorder}`, fontSize: 13, fontWeight: 600, color: isDark ? '#fbbf24' : '#92400e', lineHeight: 1.5 }}>
                  {estimate.warnings.map((w, i) => <div key={i}>{w}</div>)}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── 모바일 AI 패널 토글 ── */}
        {isMobile && !showOmniChat && tab !== 'archive' && (
          <button
            onClick={() => setShowOmniChat(true)}
            style={{
              width: '100%', padding: '10px 16px', marginTop: 16, borderRadius: 10,
              border: `1px solid ${colors.primaryBorder}`, background: colors.primaryBgLight,
              color: colors.primaryText, fontSize: 13, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontWeight: 600,
            }}
          >
            <PanelRightOpen size={16} /> AI 분석 패널 열기
          </button>
        )}

        {/* ── 리사이즈 핸들 (데스크톱 전용) ── */}
        {!isMobile && <div
          onMouseDown={(e) => {
            const startX = e.clientX;
            const startW = rightPanelWidth;
            const move = (me: MouseEvent) => {
              setRightPanelWidth(Math.max(300, Math.min(1000, startW - (me.clientX - startX))));
            };
            const up = () => {
              document.removeEventListener('mousemove', move);
              document.removeEventListener('mouseup', up);
              document.body.style.cursor = '';
            };
            document.addEventListener('mousemove', move);
            document.addEventListener('mouseup', up);
            document.body.style.cursor = 'col-resize';
          }}
          onMouseEnter={e => { e.currentTarget.style.background = colors.borderMedium; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', justifyItems: 'center', cursor: 'col-resize', transition: 'background 0.2s', borderRadius: 4 }}
          title="패널 폭 조절"
        >
          <div style={{ width: 4, height: 40, background: colors.textMuted, borderRadius: 2, margin: '0 auto' }} />
        </div>}

        {/* ── 우측 사이드바 ── */}
        <div>
          {/* Omni-Channel Agent Collaboration Panel */}
          {tab !== 'archive' && showOmniChat && (
            <div style={{ borderRadius: 12, border: `1px solid ${colors.omniBorder}`, background: colors.omniBg, marginBottom: 16, overflow: 'hidden', boxShadow: '0 4px 12px rgba(249, 115, 22, 0.1)' }}>
              <div style={{ background: 'linear-gradient(135deg, #ea580c, #f97316)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center' }}>
                  <Bot className="w-5 h-5 mr-2" />
                  Desinsight AI 분석
                </h3>
                <button
                  onClick={() => setShowOmniChat(false)}
                  style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.8)', cursor: 'pointer', fontSize: 20 }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: isMobile ? 12 : 20 }}>
                {/* Quick prompts */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 6, marginBottom: 12 }}>
                {quickPrompts.map((prompt, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setOmniChatMessage(prompt.text);
                      handleOmniChatSubmit();
                    }}
                    disabled={omniChatLoading}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 6,
                      border: `1px solid ${colors.omniBorder}`,
                      background: colors.omniBtnBg,
                      color: colors.omniText,
                      fontSize: 11,
                      cursor: omniChatLoading ? 'not-allowed' : 'pointer',
                      opacity: omniChatLoading ? 0.6 : 1,
                    }}
                  >
                    {prompt.label}
                  </button>
                ))}
              </div>

              {/* Job Status & Pipeline Card */}
              <div style={{ padding: '0 20px', marginBottom: 16 }}>
                {job && <PipelineJobCard job={job} />}
                {activeJobId && !job && (
                  <div style={{ marginBottom: 12 }}>
                    <ActionLogRoller logs={actionLog} />
                  </div>
                )}
              </div>

              {/* Chat history */}
              <div style={{ maxHeight: isMobile ? 'calc(100vh - 480px)' : 'calc(100vh - 340px)', flexGrow: 1, overflow: 'auto', marginBottom: 16, fontSize: 13, paddingRight: 4, paddingLeft: isMobile ? 12 : 20, minHeight: isMobile ? 120 : undefined }}>
                {omniChatHistory.length === 0 ? (
                  <div style={{ color: colors.textSecondary, textAlign: 'center', padding: 16 }}>
                    AI 에이전트와 협업하여<br />
                    견적을 최적화하세요
                  </div>
                ) : (
                  omniChatHistory.map((msg, i) => (
                    <div
                      key={i}
                      style={{
                        marginBottom: 8,
                        padding: '8px 12px',
                        borderRadius: 8,
                        background: msg.role === 'user' ? (isDark ? 'rgba(255,255,255,0.08)' : '#e2e8f0') : colors.card,
                        color: msg.role === 'user' ? colors.text : colors.textSecondary,
                        border: msg.role === 'assistant' ? `1px solid ${colors.border}` : 'none',
                      }}
                    >
                      {msg.content}
                    </div>
                  ))
                )}
              </div>

              {/* Multimodal Input */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px 14px',
                  borderRadius: 24,
                  border: `1px solid ${colors.omniBorder}`,
                  background: colors.inputBg,
                  overflow: 'hidden',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ color: colors.textMuted, marginRight: 8, cursor: 'pointer' }} title="이미지/도면 파일 첨부 (Ctrl+V 지원)">
                    <Paperclip size={18} />
                  </div>
                  <input
                    value={omniChatMessage}
                    onChange={e => setOmniChatMessage(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleOmniChatSubmit()}
                    onPaste={handlePaste}
                    placeholder="AI에게 질문하거나 도면 캡처를 붙여넣으세요..."
                    disabled={omniChatLoading}
                    style={{
                      flex: 1,
                      border: 'none',
                      background: 'transparent',
                      color: colors.inputText,
                      fontSize: 13,
                      outline: 'none',
                      padding: '10px 0',
                    }}
                  />
                  <div style={{ color: colors.textMuted, marginLeft: 8, cursor: 'pointer' }}>
                    <ImageIcon size={18} />
                  </div>
                </div>
                <button
                  onClick={handleOmniChatSubmit}
                  disabled={omniChatLoading || !omniChatMessage.trim()}
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: omniChatLoading || !omniChatMessage.trim() ? colors.omniBg : 'linear-gradient(135deg, #f97316, #fb923c)',
                    color: omniChatLoading || !omniChatMessage.trim() ? colors.textMuted : '#fff',
                    cursor: omniChatLoading || !omniChatMessage.trim() ? 'not-allowed' : 'pointer',
                    boxShadow: omniChatLoading || !omniChatMessage.trim() ? 'none' : '0 3px 10px rgba(249,115,22,0.3)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Send size={18} style={{ marginLeft: -2, marginTop: 1 }} />
                </button>
              </div>
              </div>
            </div>
          )}

          {/* Show omni-chat button if hidden (Not in archive tab) */}
          {tab !== 'archive' && !showOmniChat && (
            <button
              onClick={() => setShowOmniChat(true)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: 10,
                border: `1px dashed ${colors.omniBorder}`,
                background: colors.omniBg,
                color: colors.omniText,
                fontSize: 13,
                cursor: 'pointer',
                marginBottom: 16,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <MessageSquare className="w-4 h-4" />
              Omni-Agent 협업 패널 열기
            </button>
          )}

          {/* Archive Tab 전용 OmniChatPanel (최신 V2 채팅 패널) */}
          {tab === 'archive' && (
            <div style={{ position: 'sticky', top: 24 }}>
              <OmniChatPanel
                defaultAgent="boq"
                placeholder="과거 문서함 기록이나 도서관/오피스 설계에 대해 질문하세요..."
                compact={true}
              />
            </div>
          )}

          {/* 견적 요약 */}
          {estimate && (
            <div style={{ padding: 20, borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, marginBottom: 16 }}>
 <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.text, margin: '0 0 16px' }}><DollarSign className="w-4 h-4 inline-block" />견적 요약</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>총 아이템</span>
                  <span style={{ color: colors.text, fontWeight: 600 }}>{estimate.totalItems}종 / {estimate.totalQty}개</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>단가 등급</span>
                  <span style={{ color: colors.primaryText, fontWeight: 600 }}>{estimate.priceGrade === 'min' ? '최저가' : estimate.priceGrade === 'avg' ? '평균가' : estimate.priceGrade === 'high' ? '고급형' : '최고급형'}</span>
                </div>
                <div style={{ height: 1, background: colors.border, margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>공급가액</span>
                  <span style={{ color: colors.text }}>{fmt(estimate.subtotal)}원</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: colors.textSecondary }}>부가가치세</span>
                  <span style={{ color: colors.text }}>{fmt(estimate.vat)}원</span>
                </div>
                <div style={{ height: 1, background: colors.border, margin: '4px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700, alignItems: 'center' }}>
                  <span style={{ color: colors.text }}>합계</span>
                  <span style={{ color: colors.successText, fontSize: 22 }}>{fmt(estimate.grandTotal)}원</span>
                </div>
              </div>
            </div>
          )}

          {/* 원가 명세 (Cost Breakdown) */}
          {estimate && estimate.costSummary && (
            <div style={{ padding: 20, borderRadius: 12, border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.02)', marginBottom: 16 }}>
              <div 
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                onClick={() => setShowCostDetails(!showCostDetails)}
              >
                <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.successText, margin: 0 }}><BarChart3 className="w-4 h-4 inline-block mr-1" />상세 원가 명세</h3>
                <span style={{ color: colors.successText, fontSize: 12 }}>
                  {showCostDetails ? '▲ 접기' : '▼ 펼쳐보기'}
                </span>
              </div>
              
              {showCostDetails && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 16, animation: 'fadeIn 0.2s ease-out' }}>
                  {/* 1. 공장 제작비 그룹 */}
                  <div style={{ paddingBottom: 6, borderBottom: `1px solid ${colors.tableBorder}` }}>
                    <div style={{ fontSize: 11, color: colors.successText, marginBottom: 4, fontWeight: 600 }}>공장 제작비</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 자재비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalRawMaterial || 0)}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 노무비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt(((estimate.costSummary as any).totalAssembly || 0) + ((estimate.costSummary as any).totalPainting || 0))}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 가공비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt(((estimate.costSummary as any).totalCutting || 0) + ((estimate.costSummary as any).totalEdge || 0) + ((estimate.costSummary as any).totalOutsourcing || 0))}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 포장비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalPackaging || 0)}원</span>
                    </div>
                  </div>

                  {/* 2. 경비 그룹 */}
                  <div style={{ paddingBottom: 6, borderBottom: `1px solid ${colors.tableBorder}` }}>
                    <div style={{ fontSize: 11, color: colors.successText, marginBottom: 4, marginTop: 4, fontWeight: 600 }}>경비</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 운송비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalTransport || 0)}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 보양/기타잡비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalMiscExpense || 0)}원</span>
                    </div>
                  </div>

                  {/* 3. 현장설치비용 그룹 */}
                  <div style={{ paddingBottom: 6, borderBottom: `1px solid ${colors.tableBorder}` }}>
                    <div style={{ fontSize: 11, color: colors.successText, marginBottom: 4, marginTop: 4, fontWeight: 600 }}>현장분</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 소운반/양중비</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalLoading || 0)}원</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: colors.textSecondary }}>↳ 전문세팅/설치</span>
                      <span style={{ color: colors.text, fontWeight: 500 }}>{fmt((estimate.costSummary as any).totalSettingLabor || 0)}원</span>
                    </div>
                  </div>

                  {/* 4. 이윤 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                    <span style={{ color: colors.textSecondary }}>일반관리비 및 이윤</span>
                    <span style={{ color: colors.warningText, fontWeight: 600 }}>{fmt(((estimate.costSummary as any).totalGeneralMgmt || 0) + ((estimate.costSummary as any).totalProfit || 0))}원</span>
                  </div>

                  <div style={{ height: 1, background: colors.successBorder, margin: '4px 0' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 700 }}>
                    <span style={{ color: colors.successText }}>생산원가 합계</span>
                    <span style={{ color: colors.successText }}>{fmt(estimate.subtotal)}원</span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* 카테고리별 소계 */}
          {estimate && estimate.categoryBreakdown.length > 0 && (
            <div style={{ padding: 20, borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.surfaceAlt, marginBottom: 16 }}>
 <h3 style={{ fontSize: 14, fontWeight: 600, color: colors.text, margin: '0 0 12px' }}><BarChart3 className="w-4 h-4 inline-block" />카테고리별 소계</h3>
              {estimate.categoryBreakdown.map(cb => {
                const pct = Math.round(cb.totalAmount / estimate.subtotal * 100);
                return (
                  <div key={cb.category} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                      <span style={{ color: colors.text }}>{cb.label} ({cb.itemCount}종, {cb.totalQty}개)</span>
                      <span style={{ color: colors.warningText }}>{fmt(cb.totalAmount)}원</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: colors.border, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pct}%`, borderRadius: 2, background: 'linear-gradient(90deg, #f97316, #ef4444)' }} />
                    </div>
                    <div style={{ fontSize: 10, color: colors.textMuted, textAlign: 'right', marginTop: 2 }}>{pct}%</div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 중앙 단가 출처 뱃지 */}
          <div style={{ padding: isMobile ? '10px 12px' : '14px 18px', borderRadius: 12, background: colors.primaryBgLight, border: `1px solid ${colors.primaryBorder}`, fontSize: isMobile ? 11 : 13, color: colors.primaryText, display: 'flex', alignItems: 'center', gap: 12, flexDirection: isMobile ? 'column' : 'row' }}>
            <div style={{ background: '#fff', padding: 8, borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
              <Lightbulb size={20} color={colors.primaryText} />
            </div>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 2 }}>적용 단가 모델: A 프로젝트 인테리어 제작가구</div>
              <div style={{ fontSize: 11, opacity: 0.85 }}>레퍼런스 93건 / 공급가 ₩224,930,000 / 10개 핵심 카테고리 기반</div>
            </div>
          </div>
        </div>
      </main>

      {/* PDF 생성을 위한 모달 */}
      {showPdfModal && estimate && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: isMobile ? 16 : 0 }}>
          <div style={{ background: colors.card, padding: isMobile ? 16 : 24, borderRadius: 12, width: isMobile ? '100%' : 360, maxWidth: 360, color: colors.text, boxShadow: '0 10px 25px rgba(0,0,0,0.2)', border: `1px solid ${colors.border}` }}>
            <h3 style={{ marginTop: 0, marginBottom: 8, fontSize: 16 }}>🖨️ PDF 출력 옵션</h3>
            <p style={{ color: colors.textMuted, fontSize: 12, marginBottom: 20 }}>PDF에 포함할 문서 및 결재란을 설정해주세요.</p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={pdfOptions.includeSummary} onChange={(e) => setPdfOptions(prev => ({ ...prev, includeSummary: e.target.checked }))} />
                견적서 표지 및 소계 요약
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={pdfOptions.includeDetails} onChange={(e) => setPdfOptions(prev => ({ ...prev, includeDetails: e.target.checked }))} />
                제작 분해 모듈 및 일위대가 산출내역
              </label>
            </div>

            <div style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: colors.textSecondary, marginBottom: 12 }}>갑지 결재선 (실명 기입 시에만 노출)</div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>작성자 (Author)</div>
                  <input type="text" value={pdfOptions.author} onChange={e => setPdfOptions(prev => ({ ...prev, author: e.target.value }))} style={getInputStyle(colors)} placeholder="홍길동 대리" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>검토자 (Reviewer)</div>
                  <input type="text" value={pdfOptions.reviewer} onChange={e => setPdfOptions(prev => ({ ...prev, reviewer: e.target.value }))} style={getInputStyle(colors)} placeholder="이몽룡 과장" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>확인자 (Checker)</div>
                  <input type="text" value={(pdfOptions as any).checker || ''} onChange={e => setPdfOptions(prev => ({ ...prev, checker: e.target.value }))} style={getInputStyle(colors)} placeholder="최대감 부장" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginBottom: 4 }}>승인자 (Approver)</div>
                  <input type="text" value={pdfOptions.approver} onChange={e => setPdfOptions(prev => ({ ...prev, approver: e.target.value }))} style={getInputStyle(colors)} placeholder="성춘향 팀장" />
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button 
                onClick={() => setShowPdfModal(false)}
                style={{ padding: '8px 16px', borderRadius: 6, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, cursor: 'pointer', fontSize: 13 }}>
                취소
              </button>
              <button 
                onClick={() => {
                  try {
                    // Python 엔진 통신 오류 시 로컬 렌더링 폴백
                    generateEstimatePDF({ ...estimate, discount: 0, finalGrandTotal: estimate.grandTotal }, previewImage || undefined, pdfOptions);
                  } catch (e: any) {
                    setError(`PDF 출력 오류: ${e.message}`);
                  }
                  setShowPdfModal(false);
                }}
                disabled={!pdfOptions.includeSummary && !pdfOptions.includeDetails}
                style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: colors.primaryBg, color: 'white', cursor: 'pointer', fontSize: 13, minWidth: 100, opacity: (!pdfOptions.includeSummary && !pdfOptions.includeDetails) ? 0.5 : 1 }}>
                로컬 PDF 리포트 인쇄
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 엔진 프로그레스 오버레이 */}
      {engineProgress.active && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
          <div style={{ background: colors.card, padding: 32, borderRadius: 16, width: 360, color: colors.text, boxShadow: '0 20px 40px rgba(0,0,0,0.3)', border: `1px solid ${colors.border}`, textAlign: 'center' }}>
            <div style={{ padding: '12px', background: 'rgba(14,165,233,0.1)', borderRadius: '50%', width: 56, height: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
               <HardDrive size={28} color="#0ea5e9" className="animate-spin-slow" />
            </div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: 18, fontWeight: 700 }}>AI 엔진 일괄 산출 중...</h3>
            <p style={{ color: colors.primaryText, fontSize: 14, fontWeight: 600, marginBottom: 20 }}>{engineProgress.msg}</p>

            <div style={{ width: '100%', height: 6, background: colors.border, borderRadius: 3, overflow: 'hidden', marginBottom: 12 }}>
              <div style={{ 
                height: '100%', 
                background: colors.primaryText, 
                width: engineProgress.total > 0 ? `${(engineProgress.current / engineProgress.total) * 100}%` : '5%',
                transition: 'width 0.3s ease-out'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: colors.textMuted }}>
              <span>{engineProgress.step === 'diagram' ? '다이어그램 렌더링' : '데이터 처리'}</span>
              <span>{engineProgress.current} / {engineProgress.total || '?'}</span>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ── 동적 inputStyle 생성 함수 ──
const getInputStyle = (colors: any): React.CSSProperties => ({
  width: '100%',
  padding: '6px 8px',
  borderRadius: 4,
  border: `1px solid ${colors.inputBorder}`,
  background: colors.inputBg,
  color: colors.inputText,
  fontSize: 12,
});
